import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CalendarioFeriados from '../components/CalendarioFeriados';
import ExameMudancaCargo from '../components/ExameMudancaCargo';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, LabelList, CartesianGrid } from 'recharts';
import PrazoBadge from '../components/PrazoBadge';
import '../styles/painelOperador.css';

// Painel do Operador — tudo o que a operadora logada tem para fazer, num
// lugar só. Os números vêm das MESMAS views de cada módulo (o backend só
// filtra pelas empresas dela).
// Estilo "Data-Dense Dashboard" (skill ui-ux-pro-max): KPIs em linha,
// filtros por empresa / só estourado / busca, tabelas compactas ordenáveis
// com cabeçalho fixo, skeleton no carregamento, rótulos direto no gráfico.
// Cor = estado (nunca categoria); todo estado vem com ícone + texto.

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const LS_OPERADOR = 'painel_operador_escolha';
const lerEscolha = () => { try { return localStorage.getItem(LS_OPERADOR) || ''; } catch { return ''; } };
const gravarEscolha = (v) => { try { localStorage.setItem(LS_OPERADOR, v || ''); } catch { /* sem storage */ } };

// o gráfico precisa de cores literais; espelham os tokens do CSS
const CHART = { estourado: '#b3261e', alerta: '#d4a017', emdia: '#2e8b57' };
const CHART_ROTULO = { estourado: 'Estourado', alerta: 'Alerta', emdia: 'Em dia' };
const CHART_ORDEM = ['estourado', 'alerta', 'emdia'];

// Tooltip do gráfico de carga: cartão branco, um ponto colorido por estado, total no pé
const CargaTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const linha = payload[0].payload || {};
  return (
    <div style={{ background: '#fff', border: '1px solid #e6e8eb', borderRadius: 10, boxShadow: '0 6px 20px rgba(0,32,91,.12)',
      padding: '10px 12px', minWidth: 170, fontSize: 13 }}>
      <div style={{ fontWeight: 700, color: '#212529', marginBottom: 6 }}>{label}</div>
      {CHART_ORDEM.filter((k) => linha[k]).map((k) => (
        <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0', color: '#495057' }}>
          <span><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: CHART[k], marginRight: 7 }}></span>{CHART_ROTULO[k]}</span>
          <b style={{ color: '#212529', fontVariantNumeric: 'tabular-nums' }}>{linha[k]}</b>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #eef0f2', marginTop: 6, paddingTop: 6, display: 'flex', justifyContent: 'space-between', color: '#6c757d' }}>
        <span>Total</span><b style={{ color: '#212529', fontVariantNumeric: 'tabular-nums' }}>{linha.total}</b>
      </div>
    </div>
  );
};

const CargaLegenda = () => (
  <div style={{ display: 'flex', gap: 22, justifyContent: 'center', fontSize: 13.5, color: '#495057', paddingTop: 10 }}>
    {CHART_ORDEM.map((k) => (
      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 10, height: 10, borderRadius: 10, background: CHART[k], display: 'inline-block' }}></span>{CHART_ROTULO[k]}
      </span>
    ))}
  </div>
);
const STATUS_FERIAS = {
  supervisorApproval: 'Aprovação do gestor', hrApproval: 'Aprovação do RH',
  accounting: 'Contabilidade', signature: 'Assinatura',
};
const ROTAS = {
  admissoes: '/Dp/Admiss%C3%B5es', rescisoes: '/Dp/Rescis%C3%B5es', ferias: '/Dp/F%C3%A9rias',
  prorrogacoes: '/Dp/Prorroga%C3%A7%C3%A3o%20Colaborador', beneficios: '/BpoRh/ControleBeneficios',
  ponto: '/BpoRh/FechamentoPonto', exames: '/Dp/Exame%20Mensal',
  estagios: '/Dp/Controle%20de%20Est%C3%A1gio',
};
const MAX_ITENS_GRUPO = 6;

const fmtDias = (n, futuro = 'faltam', passado = 'há') => {
  if (n === null || n === undefined) return '—';
  if (n === 0) return 'hoje';
  const a = Math.abs(n);
  return n > 0 ? `${futuro} ${a} dia${a === 1 ? '' : 's'}` : `${passado} ${a} dia${a === 1 ? '' : 's'}`;
};
const brl = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
// vermelho no painel: já venceu, vence hoje, ou está a 2 dias ou menos do
// limite ('critico') — nesse ponto o processo precisa andar hoje
const estourado = (p) => ['atrasado', 'hoje', 'critico'].includes(p?.situacao);
const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
// dd/mm/aaaa -> aaaammdd, para ordenar datas como texto
const dataKey = (d) => (d && /^\d{2}\/\d{2}\/\d{4}/.test(d) ? d.slice(6, 10) + d.slice(3, 5) + d.slice(0, 2) : '');

/* ───────────── peças ───────────── */
const Estado = ({ tipo, icone, children, title }) => (
  <span className={`po-estado ${tipo}`} title={title}>{icone && <i className={`fas ${icone}`} aria-hidden="true"></i>}{children}</span>
);

const Progresso = ({ feitas, total, proxima }) => {
  const pct = total ? Math.round((feitas / total) * 100) : 0;
  return (
    <div title={proxima ? `Próxima tarefa: ${proxima}` : 'Checklist concluído'}>
      <div className="po-prog"><div className="po-prog-bar"><span style={{ width: `${pct}%` }}></span></div><small>{feitas}/{total}</small></div>
      {proxima && <div className="po-sub mt-1 text-truncate" style={{ maxWidth: 170 }}><i className="fas fa-arrow-right me-1" style={{ fontSize: '.55rem' }} aria-hidden="true"></i>{proxima}</div>}
    </div>
  );
};

// bloco de regra (modal dos prazos): as cores do Estado, mas com texto longo
// que quebra linha — o badge tem nowrap e cortava a frase
const Regra = ({ tipo, icone, children }) => (
  <div className={`po-estado ${tipo}`}
    style={{ whiteSpace: 'normal', alignItems: 'flex-start', lineHeight: 1.45, display: 'flex' }}>
    <i className={`fas ${icone}`} aria-hidden="true" style={{ marginTop: 3 }}></i>
    <span>{children}</span>
  </div>
);

const Vazio = ({ texto }) => (<div className="po-vazio"><i className="fas fa-circle-check" aria-hidden="true"></i>{texto}</div>);

const Seta = ({ to, title }) => (
  <Link to={to} className="btn btn-sm btn-light border po-seta" title={title} aria-label={title}><i className="fas fa-arrow-right" aria-hidden="true"></i></Link>
);

const Card = ({ id, icone, titulo, sub, count, countTipo, link, linkRotulo = 'Abrir módulo', indice, children }) => {
  const badge = countTipo === 'bad' ? 'bg-danger' : countTipo === 'warn' ? 'bg-warning text-dark' : countTipo === 'ok' ? 'bg-success' : 'bg-light text-dark';
  return (
    <section id={id} className="po-card po-enter" style={{ '--i': indice }} aria-labelledby={`${id}-t`}>
      <header className="po-card-head">
        <h3 id={`${id}-t`}><i className={`fas ${icone}`} aria-hidden="true"></i>{titulo}</h3>
        {count !== undefined && <span className={`badge ${badge}`}>{count}</span>}
        {sub && <span className="po-sub">{sub}</span>}
        {link && <div className="ms-auto-wrap"><Link to={link} className="btn btn-sm btn-light" title={linkRotulo}><i className="fas fa-arrow-up-right-from-square me-1" aria-hidden="true"></i>{linkRotulo}</Link></div>}
      </header>
      <div className="po-card-body">{children}</div>
    </section>
  );
};

// tabela compacta, cabeçalho fixo, ordenável por coluna (aria-sort)
const Tabela = ({ colunas, linhas, chave, ordemInicial, vazio }) => {
  const [ordem, setOrdem] = useState(ordemInicial || { col: null, dir: 'asc' });
  const ordenadas = useMemo(() => {
    const c = colunas.find(x => x.chave === ordem.col);
    if (!c?.valor) return linhas;
    const dir = ordem.dir === 'asc' ? 1 : -1;
    return [...linhas].sort((a, b) => {
      const va = c.valor(a), vb = c.valor(b);
      if (va === vb) return 0;
      if (va === null || va === undefined || va === '') return 1;
      if (vb === null || vb === undefined || vb === '') return -1;
      return (va > vb ? 1 : -1) * dir;
    });
  }, [linhas, colunas, ordem]);
  if (!linhas.length) return <Vazio texto={vazio} />;
  const alternar = (col) => setOrdem(o => (o.col === col ? { col, dir: o.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  return (
    <div className="po-scroll">
      <table className="table po-table mb-0">
        <thead>
          <tr>
            {colunas.map(c => (
              <th key={c.chave} style={c.estilo} aria-sort={ordem.col === c.chave ? (ordem.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                {c.valor ? (
                  <button type="button" onClick={() => alternar(c.chave)} title={`Ordenar por ${c.rotulo || c.chave}`}>
                    {c.rotulo}<i className={`fas ${ordem.col === c.chave ? (ordem.dir === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down') : 'fa-sort'}`} aria-hidden="true"></i>
                  </button>
                ) : c.rotulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ordenadas.map(l => <tr key={chave(l)}>{colunas.map(c => <td key={c.chave} className={c.classe}>{c.render(l)}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
};

const Grupo = ({ icone, rotulo, itens, link, cor }) => {
  const [aberto, setAberto] = useState(false);
  if (!itens.length) return null;
  const mostrar = aberto ? itens : itens.slice(0, MAX_ITENS_GRUPO);
  const resto = itens.length - mostrar.length;
  return (
    <div className="po-grupo">
      <div className="po-grupo-head">
        <i className={`fas ${icone} po-texto-${cor}`} aria-hidden="true"></i>{rotulo}
        <span className={`badge rounded-pill ${cor === 'bad' ? 'bg-danger' : 'bg-warning text-dark'}`}>{itens.length}</span>
        <Link to={link}>abrir <i className="fas fa-arrow-right ms-1" style={{ fontSize: '.55rem' }} aria-hidden="true"></i></Link>
      </div>
      {mostrar.map((it, i) => (
        <Link key={i} to={it.link} className="po-grupo-item" title={it.title || it.sub}><span className="text-truncate">{it.texto}</span><span className="po-sub">{it.sub}</span></Link>
      ))}
      {resto > 0 && <button type="button" className="po-link-mais" onClick={() => setAberto(true)}>… e mais {resto}</button>}
      {aberto && itens.length > MAX_ITENS_GRUPO && <button type="button" className="po-link-mais" onClick={() => setAberto(false)}>mostrar menos</button>}
    </div>
  );
};

const Skeleton = () => (
  <div aria-busy="true" aria-label="Carregando o painel">
    <div className="po-kpis">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="po-skel po-skel-kpi"></div>)}</div>
    <div className="po-skel po-skel-card mb-2" style={{ height: 120 }}></div>
    <div className="row g-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="col-xl-6"><div className="po-skel po-skel-card"></div></div>)}</div>
  </div>
);

/* ───────────── página ───────────── */
// rescisão atrasada há mais disso (e sem finalização pedida) → pop-up ao abrir o painel
const ATRASO_SUGERE_FINALIZACAO = 10;

const PainelOperador = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [escolha, setEscolha] = useState(lerEscolha);
  const [mesPonto, setMesPonto] = useState('');
  const [abaFerias, setAbaFerias] = useState('operador');
  const [ponto, setPonto] = useState({});
  const [pontoAberto, setPontoAberto] = useState(null);
  const [prazosAberto, setPrazosAberto] = useState(false);
  // pop-up: rescisão atrasada há mais disso (sem finalização pedida) sugere pedir
  // à gestora a finalização manual — morava em /Dp/Rescisões, veio para cá (13/10/2026)
  const [sugestaoFinalizar, setSugestaoFinalizar] = useState(null);
  const [calFeriados, setCalFeriados] = useState(false);   // modal do calendário de feriados
  const sugestaoVista = useRef(false);
  const cancelPonto = useRef(0);

  useEffect(() => {
    if (sugestaoVista.current || !dados?.rescisoes?.length) return;
    sugestaoVista.current = true;
    const chave = `bporh_sugere_finalizacao_${new Date().toLocaleDateString('pt-BR')}`;
    try { if (sessionStorage.getItem(chave)) return; } catch (e) { /* sem storage: mostra */ }
    const lista = (dados.rescisoes || [])
      .filter((r) => r.prazo?.situacao === 'atrasado' && -(r.prazo.dias || 0) > ATRASO_SUGERE_FINALIZACAO)
      .filter((r) => !['solicitada', 'finalizada', 'devolvida'].includes(r.finalizacao))
      .map((r) => ({ ...r, atraso: -(r.prazo.dias || 0) }))
      .sort((a, b) => b.atraso - a.atraso);
    if (lista.length) setSugestaoFinalizar(lista);
  }, [dados]); // eslint-disable-line react-hooks/exhaustive-deps

  const fecharSugestao = () => {
    try { sessionStorage.setItem(`bporh_sugere_finalizacao_${new Date().toLocaleDateString('pt-BR')}`, '1'); } catch (e) { /* ok */ }
    setSugestaoFinalizar(null);
  };
  // filtros (a skill lista "sem filtro" como anti-padrão de dashboard)
  const [empresasSel, setEmpresasSel] = useState([]);   // vazio = todas
  const [soEstourado, setSoEstourado] = useState(false);
  const [busca, setBusca] = useState('');

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    setErro('');
    try {
      const params = {};
      if (escolha) params.operador = escolha;
      if (mesPonto) params.mes = mesPonto;
      const res = await axios.get(`${API}/api/bporh/painel-operador/`, { params, headers: headers() });
      setDados(res.data);
      setEmpresasSel([]);
    } catch (e) {
      setErro(e?.response?.data?.detalhes || e?.response?.data?.erro || 'Não foi possível montar o painel');
    } finally {
      setCarregando(false);
    }
  }, [escolha, mesPonto]);

  useEffect(() => { carregar(); }, [carregar]);

  // ponto: uma empresa por vez (APIs dos provedores são lentas)
  useEffect(() => {
    if (!dados?.ponto?.length) return undefined;
    const meu = ++cancelPonto.current;
    const mes = dados.mes_ponto;
    setPonto(Object.fromEntries(dados.ponto.map(p => [p.cliente_id, { carregando: true }])));
    (async () => {
      for (const p of dados.ponto) {
        if (cancelPonto.current !== meu) return;
        try {
          const r = await axios.get(`${API}/api/bporh/painel-operador/ponto/${p.cliente_id}/`, { params: { mes }, headers: headers() });
          if (cancelPonto.current !== meu) return;
          setPonto(prev => ({ ...prev, [p.cliente_id]: { dados: r.data } }));
        } catch (e) {
          if (cancelPonto.current !== meu) return;
          setPonto(prev => ({ ...prev, [p.cliente_id]: { erro: e?.response?.data?.erro || 'Falhou' } }));
        }
      }
    })();
    return undefined;
  }, [dados]);

  const trocarOperador = (v) => { gravarEscolha(v); setEscolha(v); };
  const alternarEmpresa = (id) => setEmpresasSel(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));

  const semEscopo = dados?.escopo === 'nenhum';
  const estagioDias = dados?.prazos?.estagio_dias ?? 15;
  const estagioTetoDias = dados?.prazos?.estagio_teto_dias ?? 30;
  const tetoAnos = dados?.prazos?.estagio_teto_anos ?? 2;
  const ferias = useMemo(() => dados?.ferias || { operador: [], gestor: [], a_vencer: [] }, [dados]);

  // ── filtro aplicado em todas as listas ──
  const passa = useCallback((item, ehEstourado) => {
    if (empresasSel.length && !empresasSel.includes(item.cliente_id)) return false;
    if (soEstourado && !ehEstourado) return false;
    if (busca && !norm(`${item.nome || ''} ${item.cliente || ''} ${item.rotulo || ''}`).includes(norm(busca))) return false;
    return true;
  }, [empresasSel, soEstourado, busca]);

  const f = useMemo(() => {
    if (!dados || semEscopo) return null;
    return {
      admissoes: (dados.admissoes || []).filter(a => passa(a, estourado(a.prazo))),
      rescisoes: (dados.rescisoes || []).filter(a => passa(a, estourado(a.prazo))),
      // fechamento de folha: prazo por empresa (alerta a 3 dias, vermelho quando passa)
      folhas: (dados.folhas || []).filter(x => passa(x, estourado(x.prazo))),
      // fechamento do mês anterior ao cliente: até o dia 5
      fechamentos: (dados.fechamentos || []).filter(x => passa(x, estourado(x))),
      ferOperador: ferias.operador.filter(x => passa(x, false)),
      ferGestor: ferias.gestor.filter(x => passa(x, false)),
      // mesma régua da tela /Dp/Férias: 'a vencer' = janela do Solicitar até,
      // sem solicitação; concessivo já estourado é passivo e fica à parte
      ferVencer: ferias.a_vencer.filter(x => passa(x, false)),
      ferEstourados: (ferias.estourados || []).filter(x => passa(x, true)),
      prorrogacoes: (dados.prorrogacoes || []).filter(p => passa(p, (p.proximo?.vencido && !p.proximo?.avisado_em) || p.sem_tipo)),
      beneficios: (dados.beneficios || []).filter(b => passa(b, estourado(b))),
      // termos de prorrogação sem PDF: 'estourado' = 1º período já venceu
      contratos: (dados.contratos || []).filter(c => passa(c, (c.marcos || []).some(m => m.pendente_contrato && m.vencido))),
      // exame periódico: o backend já manda só quem venceu — todo item é 'estourado'
      exames: (dados.exames || []).filter(x => passa(x, true)),
      // financeiro: recorrências mensais pendentes (vencendo em até 3 dias ou
      // atrasadas) — a busca casa pelo título da tarefa
      finRec: [
        ...(dados.financeiro_pagamentos || []).map(x => ({ ...x, oque: x.tipo_rotulo, nome: x.colaborador,
          detalhe: x.evento ? `${x.evento_rotulo} ${x.evento}` : '', data: x.pagar_ate, chaveFin: `${x.tipo}-${x.convenia_id}-${x.evento}` })),
        ...(dados.financeiro_recorrencias || []).map(x => ({ ...x, oque: 'Recorrência', nome: x.titulo,
          detalhe: `todo dia ${x.dia}`, data: x.vence_em, chaveFin: `rec-${x.titulo}-${x.vence_em}` })),
      ].filter(x => passa({ ...x, rotulo: x.nome }, (x.dias_para_prazo ?? 0) < 0)),
      pmFerias: (dados.pontomais_ferias || []).filter(x => passa({ ...x, rotulo: x.nome }, x.tipo === 'faltando')),
      // estágio: termo ou teto de 2 anos em 15 dias/vencido = estourado;
      // sem contrato cadastrado é regularização, não prazo
      estagios: (dados.estagios || []).filter(x => passa(x, x.motivo !== 'sem_contrato')),
      ponto: (dados.ponto || []).filter(p => !empresasSel.length || empresasSel.includes(p.cliente_id)),
    };
  }, [dados, semEscopo, ferias, passa, empresasSel]);

  const pontoTotais = useMemo(() => {
    let faltam = 0, avisados = 0, pendentes = 0, carregandoN = 0;
    // por EMPRESA: quantas ainda têm colaborador sem aviso e quantas já avisaram todos
    let empresasFaltam = 0, empresasOk = 0;
    (f?.ponto || []).forEach(p => {
      const st = ponto[p.cliente_id];
      if (!st) return;
      if (st.carregando) carregandoN += 1;
      else if (st.dados && !st.dados.indisponivel) {
        faltam += st.dados.faltam || 0; avisados += st.dados.avisados || 0; pendentes += st.dados.com_pendencia || 0;
        if ((st.dados.faltam || 0) > 0) empresasFaltam += 1; else if ((st.dados.com_pendencia || 0) > 0) empresasOk += 1;
      }
    });
    return { faltam, avisados, pendentes, carregando: carregandoN, empresasFaltam, empresasOk };
  }, [ponto, f]);

  // ── KPIs derivados das listas filtradas (os números seguem o filtro) ──
  const k = useMemo(() => {
    if (!f) return {};
    const benPend = f.beneficios.filter(b => b.situacao !== 'entregue');
    return {
      admNovas: f.admissoes.filter(a => a.fase === 'nova').length,
      admAndamento: f.admissoes.filter(a => a.fase === 'andamento').length,
      admEstouradas: f.admissoes.filter(a => estourado(a.prazo)).length,
      resc: f.rescisoes.length, rescEstouradas: f.rescisoes.filter(a => estourado(a.prazo)).length,
      folhas: f.folhas.length, folhasAbertas: f.folhas.filter(x => !x.entregue).length,
      folhasAtrasadas: f.folhas.filter(x => estourado(x.prazo)).length,
      fechEnviados: f.fechamentos.filter(x => x.situacao === 'entregue').length,
      fechPend: f.fechamentos.filter(x => !['entregue', 'nao_cobrado'].includes(x.situacao)).length,
      fechAtrasados: f.fechamentos.filter(x => estourado(x)).length,
      ferFila: f.ferOperador.length + f.ferGestor.length,
      ferFuturo: f.ferVencer.length, ferVencidas: f.ferEstourados.length,
      ferSemMotivo: f.ferEstourados.filter(x => x.motivo_pendente).length,
      benPend: benPend.length, benTotal: f.beneficios.length, benAtrasados: benPend.filter(b => estourado(b)).length,
      pro: f.prorrogacoes.length, proVencidas: f.prorrogacoes.filter(p => p.proximo?.vencido && !p.proximo?.avisado_em).length,
      proSemTipo: f.prorrogacoes.filter(p => p.sem_tipo).length,
      contratos: f.contratos.length,
      contratosVencidos: f.contratos.filter(c => (c.marcos || []).some(m => m.pendente_contrato && m.vencido)).length,
      exames: f.exames.length,
      examesAgendados: f.exames.filter(x => x.agendado_para).length,
      estagios: f.estagios.length,
      estagiosTermo: f.estagios.filter(x => x.motivo === 'termo').length,
      estagiosTeto: f.estagios.filter(x => x.motivo === 'teto').length,
      estagiosSemContrato: f.estagios.filter(x => x.motivo === 'sem_contrato').length,
    };
  }, [f]);

  // ── faixa de alertas ──
  const alertas = useMemo(() => {
    const critico = { admissoes: [], rescisoes: [], ferias: [], prorrogacoes: [], beneficios: [], contratos: [], exames: [], estagios: [] };
    const atencao = { admissoes: [], rescisoes: [], ferias: [], prorrogacoes: [], beneficios: [], contratos: [], estagios: [] };
    if (!f) return { critico, atencao, nCritico: 0, nAtencao: 0 };
    f.admissoes.forEach(a => {
      const it = { texto: `${a.nome} · ${a.cliente}`, link: a.link };
      if (estourado(a.prazo)) critico.admissoes.push({ ...it,
        sub: a.prazo.situacao === 'hoje' ? 'entra hoje'
          : a.prazo.situacao === 'critico' ? `entra em ${a.prazo.dias}d`
            : `atrasada ${Math.abs(a.prazo.dias)}d` });
      else if (a.prazo?.situacao === 'alerta') atencao.admissoes.push({ ...it, sub: `entra em ${a.prazo.dias}d` });
    });
    f.rescisoes.forEach(a => {
      const it = { texto: `${a.nome} · ${a.cliente}`, link: a.link };
      if (estourado(a.prazo)) critico.rescisoes.push({ ...it,
        sub: a.prazo.situacao === 'hoje' ? 'vence hoje'
          : a.prazo.situacao === 'critico' ? `vence em ${a.prazo.dias}d`
            : `atrasada ${Math.abs(a.prazo.dias)}d` });
      else if (a.prazo?.situacao === 'alerta') atencao.rescisoes.push({ ...it, sub: `vence em ${a.prazo.dias}d` });
    });
    // concessivo estourado: pagamento em dobro (art. 137) — é crítico, e a
    // operadora precisa registrar o motivo do atraso. Aparece no topo do painel.
    f.ferEstourados.forEach(x => critico.ferias.push({
      texto: `${x.nome} · ${x.cliente}`, link: x.link,
      sub: `solicitar até ${x.prazo_ate || x.concessivo_fim} · ${fmtDias(x.prazo_dias, 'faltam', 'atrasado há')}`
        + (x.motivo_pendente ? ' · falta o motivo do atraso' : ''),
    }));
    // dentro da janela de 30 dias e ainda sem solicitação: vermelho — depois
    // desse prazo as férias não cabem mais no concessivo sem virar passivo
    f.ferVencer.forEach(x => critico.ferias.push({ texto: `${x.nome} · ${x.cliente}`, link: x.link, sub: `solicitar até ${x.prazo_ate || x.concessivo_fim} · ${fmtDias(x.prazo_dias)}` }));
    // solicitação em aberto com as férias começando em até 30 dias: também é
    // vermelho — sem a aprovação concluída o colaborador não sai de férias
    [...f.ferOperador, ...f.ferGestor].forEach(x => critico.ferias.push({
      texto: `${x.nome} · ${x.cliente}`, link: x.link,
      sub: `início ${x.inicio} · ${fmtDias(x.dias_para_inicio, 'faltam', 'começou há')}`
        + ` · ${x.quem === 'gestor' ? 'aguardando o gestor' : 'pendente comigo'}`,
    }));
    f.prorrogacoes.forEach(p => {
      const it = { texto: `${p.nome} · ${p.cliente}`, link: p.link };
      if (p.proximo?.vencido && !p.proximo?.avisado_em) critico.prorrogacoes.push({ ...it, sub: `${p.proximo.numero}º período venceu ${p.proximo.fim}` });
      // sem tipo na Convenia: crítico quando o período já está vencendo (não dá
      // para avisar o gestor sem saber se é 30+60 ou 45+45); antes disso, atenção
      else if (p.sem_tipo && p.proximo?.alerta) critico.prorrogacoes.push({ ...it, sub: `cadastrar na Convenia · ${p.proximo.numero}º período em ${p.proximo.dias_para_fim}d` });
      else if (p.sem_tipo) atencao.prorrogacoes.push({ ...it, sub: `cadastrar na Convenia · ${p.proximo?.numero}º período em ${p.proximo?.dias_para_fim}d` });
      else if (p.proximo?.alerta && !p.proximo?.avisado_em) atencao.prorrogacoes.push({ ...it, sub: `${p.proximo.numero}º período em ${p.proximo.dias_para_fim}d` });
    });
    f.beneficios.forEach(b => {
      const it = { texto: `${b.rotulo} · ${b.cliente}`, link: b.link };
      if (estourado(b)) critico.beneficios.push({ ...it, sub: b.situacao === 'hoje' ? 'dia limite é hoje' : `passou do dia ${b.limite.slice(0, 2)}` });
      else if (b.situacao === 'alerta') atencao.beneficios.push({ ...it, sub: `até ${b.limite}` });
    });
    f.contratos.forEach(c => {
      const m1 = (c.marcos || []).find(m => m.pendente_contrato) || {};
      const it = { texto: `${c.nome} · ${c.cliente}`, link: c.link };
      if (m1.vencido) critico.contratos.push({ ...it, sub: `termo sem PDF · 1º período venceu ${m1.fim}` });
      else atencao.contratos.push({ ...it, sub: `termo sem PDF · 1º período em ${m1.dias_para_fim}d` });
    });
    // exame periódico vencido: sempre crítico (NR-7); quem já tem exame
    // agendado continua na lista, mas com a data ao lado
    f.exames.forEach(x => critico.exames.push({
      texto: `${x.nome} · ${x.cliente}`, link: x.link,
      sub: `venceu ${x.vencimento} · ${fmtDias(x.dias_para_vencer, 'faltam', 'há')}`
        + (x.agendado_para ? ` · agendado ${x.agendado_para}` : ''),
    }));
    // estágio: o backend só manda quem está a 15 dias, hoje ou vencido (termo
    // ou teto de 2 anos) e quem está sem contrato. Teto = não cabe renovação.
    f.estagios.forEach(x => {
      const it = { texto: `${x.nome} · ${x.cliente}`, link: x.link,
        title: x.teto_estimado ? 'sem admissão na Convenia — calculado pelo primeiro termo' : undefined };
      const inst = x.instituicao ? ` · ${x.instituicao}` : '';
      if (x.motivo === 'teto') {
        const est = x.teto_estimado ? '*' : '';
        critico.estagios.push({ ...it, sub: (x.dias < 0 ? `limite de ${tetoAnos} anos atingido há ${Math.abs(x.dias)}d${est}`
          : x.dias === 0 ? `limite de ${tetoAnos} anos é hoje${est}` : `limite de ${tetoAnos} anos em ${x.dias}d${est}`) + ' · não cabe renovação' + inst });
      } else if (x.motivo === 'termo') {
        critico.estagios.push({ ...it, sub: (x.dias < 0 ? `termo vencido há ${Math.abs(x.dias)}d`
          : x.dias === 0 ? 'termo vence hoje' : `termo vence em ${x.dias}d`) + inst });
      } else atencao.estagios.push({ ...it, sub: `sem contrato cadastrado${inst}` });
    });
    const soma = (o) => Object.values(o).reduce((n, l) => n + l.length, 0);
    return { critico, atencao, nCritico: soma(critico), nAtencao: soma(atencao) };
  }, [f, tetoAnos]);

  // ── gráfico: carga por módulo, empilhada por estado, ordenada pelo total ──
  const carga = useMemo(() => {
    if (!f) return [];
    const prazo = (lista) => ({
      estourado: lista.filter(a => estourado(a.prazo)).length,
      alerta: lista.filter(a => a.prazo?.situacao === 'alerta').length,
      emdia: lista.filter(a => !a.prazo || a.prazo.situacao === 'ok').length,
    });
    const pend = f.beneficios.filter(b => b.situacao !== 'entregue');
    const linhas = [
      { modulo: 'Admissões', ...prazo(f.admissoes) },
      { modulo: 'Rescisões', ...prazo(f.rescisoes) },
      { modulo: 'Folha', ...prazo(f.folhas.filter(x => !x.entregue)) },
      { modulo: 'Fechamento ao cliente', estourado: k.fechAtrasados || 0,
        alerta: f.fechamentos.filter(x => x.situacao === 'alerta').length,
        emdia: f.fechamentos.filter(x => x.situacao === 'ok').length },
      { modulo: 'Férias na fila', estourado: k.ferFila || 0, alerta: 0, emdia: 0 },
      { modulo: 'Férias a vencer', estourado: (k.ferVencidas || 0) + (k.ferFuturo || 0), alerta: 0, emdia: 0 },
      { modulo: 'Ponto', estourado: 0, alerta: pontoTotais.faltam, emdia: pontoTotais.avisados },
      { modulo: 'VA · HO · VT', estourado: pend.filter(b => estourado(b)).length, alerta: pend.filter(b => b.situacao === 'alerta').length, emdia: pend.filter(b => b.situacao === 'ok').length },
      { modulo: 'Experiência', estourado: k.proVencidas || 0, alerta: (k.pro || 0) - (k.proVencidas || 0), emdia: 0 },
      { modulo: 'Termos s/ PDF', estourado: k.contratosVencidos || 0, alerta: (k.contratos || 0) - (k.contratosVencidos || 0), emdia: 0 },
      { modulo: 'Exame periódico', estourado: k.exames || 0, alerta: 0, emdia: 0 },
      { modulo: 'Estágio', estourado: (k.estagiosTermo || 0) + (k.estagiosTeto || 0), alerta: k.estagiosSemContrato || 0, emdia: 0 },
    ].map(l => ({ ...l, total: l.estourado + l.alerta + l.emdia }));
    return linhas.sort((a, b) => b.total - a.total);
  }, [f, k, pontoTotais]);

  const irPara = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const kpi = (id, icone, rotulo, valor, sub, cor) => (
    <button type="button" key={`${id}-${rotulo}`} className={`po-kpi c-${cor} ${!valor ? 'po-zero' : ''}`} onClick={() => irPara(id)} title={`Ir para ${rotulo}`}>
      <div className="po-kpi-rotulo"><i className={`fas ${icone}`} aria-hidden="true"></i>{rotulo}</div>
      <div className="po-kpi-valor">{valor}</div>
      <div className="po-kpi-sub">{sub}</div>
    </button>
  );

  const tituloEscopo = dados?.operador ? dados.operador.nome : (semEscopo ? 'selecione a operadora' : 'Todas as operadoras');
  const empresasComItens = useMemo(() => {
    if (!dados || semEscopo) return [];
    const cont = {};
    const soma = (lista) => lista.forEach(x => { cont[x.cliente_id] = (cont[x.cliente_id] || 0) + 1; });
    soma(dados.admissoes || []); soma(dados.rescisoes || []); soma(ferias.operador); soma(ferias.gestor); soma(ferias.a_vencer); soma(ferias.estourados || []);
    soma(dados.prorrogacoes || []); soma((dados.beneficios || []).filter(b => b.situacao !== 'entregue')); soma(dados.contratos || []);
    soma(dados.exames || []); soma(dados.estagios || []);
    return (dados.clientes || []).map(c => ({ ...c, n: cont[c.id] || 0 }));
  }, [dados, semEscopo, ferias]);
  const filtroAtivo = empresasSel.length > 0 || soEstourado || busca;

  /* colunas das tabelas */
  const colPessoa = (subFn) => ({ chave: 'nome', rotulo: 'Colaborador', valor: (l) => norm(l.nome), render: (l) => (<><div className="po-nome">{l.nome}</div><div className="po-sub">{subFn(l)}</div></>) });
  const colEmpresa = { chave: 'cliente', rotulo: 'Empresa', valor: (l) => norm(l.cliente), render: (l) => <span className="fw-semibold">{l.cliente}</span> };
  // ─── férias Convenia × PontoMais: enviar um colaborador / reler o ponto ───
  const [pmOcupado, setPmOcupado] = useState('');
  const [pmMsg, setPmMsg] = useState('');
  const [pmIgnorar, setPmIgnorar] = useState(null);   // {linha, motivo} — confirmação do Ignorar
  // ─── troca de cargo na Convenia → e-mail à medicina (exame de mudança de função) ───
  const [mudCargo, setMudCargo] = useState(null);     // pendência aberta no modal
  const [mudCargoMsg, setMudCargoMsg] = useState('');
  const acaoPmFerias = (corpo, chave) => {
    setPmOcupado(chave); setPmMsg('');
    axios.post(`${API}/api/bporh/pontomais-ferias/`, corpo, { headers: headers(), timeout: 240000 })
      .then((r) => { setPmMsg(r.data.mensagem || 'Feito'); carregar(true); })
      .catch((e) => setPmMsg(e?.response?.data?.mensagem || 'Não foi possível falar com o PontoMais'))
      .finally(() => setPmOcupado(''));
  };
  const colSeta = (titulo) => ({ chave: 'seta', rotulo: '', estilo: { width: 44 }, render: (l) => <Seta to={l.link} title={titulo} /> });
  const colPrazo = { chave: 'prazo', rotulo: 'Prazo', valor: (l) => (l.prazo ? l.prazo.dias : 9999), render: (l) => (<>{<PrazoBadge prazo={l.prazo} />}{l.contrato_pendente && <div className="mt-1"><Estado tipo="warn" icone="fa-file-signature">sem contrato</Estado></div>}</>) };
  const colFase = { chave: 'fase', rotulo: 'Fase', valor: (l) => l.fase, render: (l) => (<><Estado tipo={l.fase === 'nova' ? 'hot' : 'neutro'} icone={l.fase === 'nova' ? 'fa-star' : 'fa-list-check'}>{l.fase === 'nova' ? 'Nova' : 'Em andamento'}</Estado>{l.etapa && <div className="po-sub mt-1">{l.etapa}</div>}</>) };
  const colCheck = { chave: 'check', rotulo: 'Checklist', valor: (l) => (l.total ? l.feitas / l.total : 0), render: (l) => <Progresso feitas={l.feitas} total={l.total} proxima={l.proxima} /> };

  return (
    <div className="painel-op">
      <div className="container-fluid mt-4 shadow p-4 rounded bg-white">
        {/* Cabeçalho (padrão do app) */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center">
            <i className="fas fa-gauge-high header-icon me-2" aria-hidden="true"></i>
            <h2 className="titulo-pagina m-0">BPO RH — Painel do Operador{dados && <span className="text-muted"> · {tituloEscopo}</span>}</h2>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {dados && (
              <select className="po-select" value={escolha} onChange={(e) => trocarOperador(e.target.value)} title="De quem é este painel" aria-label="De quem é este painel">
                <option value="">{dados.operador && !escolha ? `Minhas empresas (${dados.operador.nome})` : 'Selecione a operadora…'}</option>
                {(dados.operadores || []).map(o => <option key={o.id} value={String(o.id)}>{o.nome}</option>)}
                <option value="todos">Todas as operadoras</option>
              </select>
            )}
            <input type="month" className="po-select" value={mesPonto || dados?.mes_ponto || ''} onChange={(e) => setMesPonto(e.target.value)} title="Mês do resumo de ponto" aria-label="Mês do resumo de ponto" />
            <button className="btn btn-outline-secondary" onClick={() => setPrazosAberto(true)}
              title="Como cada prazo é calculado e quando fica amarelo ou vermelho">
              <i className="fas fa-circle-question me-2"></i>Entender os prazos
            </button>
            <button className="btn btn-outline-primary" onClick={() => carregar(true)} disabled={carregando} title="Recarregar sem sair da página">
              <i className={`fas fa-rotate me-2 ${carregando ? 'fa-spin' : ''}`} aria-hidden="true"></i>Atualizar
            </button>
          </div>
        </div>

        {erro && <div className="alert alert-danger py-2 d-flex align-items-center gap-2" role="alert"><i className="fas fa-triangle-exclamation" aria-hidden="true"></i>{erro}
          <button className="btn btn-sm btn-outline-danger ms-auto" onClick={() => carregar()}>Tentar de novo</button></div>}

        {carregando && !dados && <Skeleton />}

        {/* ─── Modal: como cada prazo é contado ─── */}
        {prazosAberto && (() => {
          const pz = dados?.prazos || {};
          const cri = pz.critico_dias ?? 2;
          const ale = pz.alerta_dias ?? 3;
          const fer = pz.ferias_dias ?? ferias.dias_alerta ?? 30;
          const REGRAS = [
            {
              icone: 'fa-user-plus', titulo: 'Admissões', cor: 'cyan',
              prazo: 'A data de admissão do colaborador.',
              detalhe: 'Todo o checklist tem de estar concluído até o dia em que a pessoa entra.',
              vermelho: `Faltam ${cri} dias ou menos, vence hoje, ou já passou da data — com o checklist em aberto.`,
              amarelo: `Faltam ${ale} dias.`,
            },
            {
              icone: 'fa-user-minus', titulo: 'Rescisões', cor: 'cyan',
              prazo: `${pz.rescisao_dias ?? 10} dias corridos do desligamento (art. 477 da CLT).`,
              detalhe: 'Desligamento no último dia do mês conta até o 5º dia útil do mês seguinte. '
                + 'Se o prazo cai em sábado ou domingo, antecipa para a sexta.',
              vermelho: `Faltam ${cri} dias ou menos, vence hoje, ou já passou — com o checklist em aberto.`,
              amarelo: `Faltam ${ale} dias.`,
            },
            {
              icone: 'fa-umbrella-beach', titulo: 'Férias — a solicitar', cor: 'navy',
              prazo: 'A data "Solicitar até" da Convenia, do período aquisitivo.',
              detalhe: 'Depois dela as férias não cabem mais dentro do período concessivo sem virar passivo '
                + '(pagamento em dobro, art. 137).',
              vermelho: `Faltam ${fer} dias ou menos para o "Solicitar até", sem nenhuma solicitação aberta. `
                + 'Passou da data, além do vermelho o motivo do atraso passa a ser obrigatório.',
              amarelo: 'Não há faixa amarela: dentro da janela já é vermelho.',
            },
            {
              icone: 'fa-user-check', titulo: 'Férias — na fila de aprovação', cor: 'navy',
              prazo: 'A data de início das férias solicitadas.',
              detalhe: 'Conta a aprovação que ainda depende de você ou do gestor do cliente. '
                + 'A etapa Contabilidade fica fora — não é executada por nós.',
              vermelho: `As férias começam em ${fer} dias ou menos e a aprovação não foi concluída.`,
              amarelo: 'Não há faixa amarela.',
            },
            {
              icone: 'fa-user-clock', titulo: 'Experiência (prorrogação)', cor: 'warn',
              prazo: 'O fim do 1º e do 2º período de experiência.',
              detalhe: 'O gestor precisa ser avisado antes para decidir prorrogar ou desligar.',
              vermelho: 'O período já venceu e o gestor não foi avisado. Também entra em vermelho quem está '
                + 'sem o tipo de experiência cadastrado na Convenia com o período já na janela.',
              amarelo: `Faltam ${pz.prorrogacao_dias ?? 15} dias ou menos para o fim do período, sem aviso ao gestor.`,
            },
            {
              icone: 'fa-utensils', titulo: 'VA · Home Office · VT', cor: 'warn',
              prazo: `Dia ${pz.beneficio_dia ?? 25} do mês, para a competência seguinte.`,
              detalhe: 'A recarga é comprada adiantada: o lançamento precisa estar feito antes do fechamento do pedido.',
              vermelho: `Passou do dia ${pz.beneficio_dia ?? 25} sem lançar, ou o dia ${pz.beneficio_dia ?? 25} é hoje.`,
              amarelo: `Faltam ${pz.beneficio_alerta_dias ?? 3} dias ou menos para o dia ${pz.beneficio_dia ?? 25}.`,
            },
            {
              icone: 'fa-file-invoice-dollar', titulo: 'Folha de pagamento', cor: 'cyan',
              prazo: 'O dia combinado com cada cliente (cadastrado no módulo de Folha).',
              detalhe: 'Entregue = folha fechada em todas as filiais da empresa.',
              vermelho: `Faltam ${cri} dias ou menos, vence hoje, ou já passou sem fechar.`,
              amarelo: `Faltam ${ale} dias.`,
            },
            {
              icone: 'fa-file-signature', titulo: 'Termo de prorrogação sem PDF', cor: 'hot',
              prazo: 'O fim do 1º período de experiência — é quando o termo é assinado.',
              detalhe: 'Só cobra quem já está na janela: antes disso não há o que anexar.',
              vermelho: 'O 1º período já venceu e o PDF não foi anexado.',
              amarelo: `O 1º período vence em ${pz.prorrogacao_dias ?? 15} dias ou menos, sem o PDF.`,
            },
            {
              icone: 'fa-graduation-cap', titulo: 'Estágio', cor: 'hot',
              prazo: `Dois relógios: o fim do termo de compromisso vigente e o limite legal de ${pz.estagio_teto_anos ?? 2} anos `
                + 'na mesma empresa (Lei 11.788, art. 11).',
              detalhe: 'O termo cabe renovar por aditivo; o limite legal NÃO cabe — ali o gestor decide entre encerrar e efetivar. '
                + 'O limite conta da data de admissão da Convenia; sem admissão, do primeiro termo (*).',
              vermelho: `Fim do termo em ${pz.estagio_dias ?? 15} dias ou menos (cabe renovar), ou limite legal de `
                + `${pz.estagio_teto_anos ?? 2} anos em ${pz.estagio_teto_dias ?? 30} dias ou menos (não cabe renovar) — incluindo hoje e vencido.`,
              amarelo: 'Estagiário da Convenia sem termo cadastrado — é regularização, não prazo.',
            },
            {
              icone: 'fa-fingerprint', titulo: 'Ponto', cor: 'warn',
              prazo: 'Não tem data: é o fechamento do mês em curso.',
              detalhe: 'Conta quem não bateu ponto em dia útil ou bateu número ímpar de vezes e ainda não foi avisado.',
              vermelho: 'Não se aplica.',
              amarelo: 'Há colaborador com pendência de batida sem aviso enviado.',
            },
          ];
          return (
            <div className="modal fade show d-block" tabIndex="-1"
              style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10002 }}
              onClick={(e) => { if (e.target === e.currentTarget) setPrazosAberto(false); }}>
              <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable"
                style={{ maxWidth: 'min(1500px, 96vw)', width: '96vw' }}>
                <div className="modal-content shadow-lg" style={{ minHeight: 'min(880px, 92vh)' }}>
                  <div className="modal-header bg-white">
                    <div>
                      <h5 className="modal-title" style={{ color: '#212529' }}>
                        <i className="fas fa-circle-question me-2" style={{ color: '#00b5cc' }}></i>
                        Como os prazos são contados
                      </h5>
                      <div className="small" style={{ color: '#6c757d' }}>
                        A mesma régua vale no painel e na tela de cada módulo. Tudo o que está
                        em vermelho precisa andar hoje.
                      </div>
                    </div>
                    <button type="button" className="btn-close" onClick={() => setPrazosAberto(false)}></button>
                  </div>
                  <div className="modal-body">
                    <div className="d-flex flex-wrap gap-3 mb-3">
                      <Estado tipo="bad" icone="fa-circle-exclamation">vermelho: precisa de você hoje</Estado>
                      <Estado tipo="warn" icone="fa-triangle-exclamation">amarelo: pode ficar para outro momento</Estado>
                      <Estado tipo="neutro" icone="fa-clock">sem cor: dentro do prazo</Estado>
                      <Estado tipo="ok" icone="fa-circle-check">verde: entregue</Estado>
                    </div>
                    <div className="table-responsive">
                      <table className="table po-table align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: '16%' }}>Atividade</th>
                            <th style={{ width: '30%' }}>De onde sai o prazo</th>
                            <th style={{ width: '30%' }}>Fica vermelho quando</th>
                            <th style={{ width: '24%' }}>Fica amarelo quando</th>
                          </tr>
                        </thead>
                        <tbody>
                          {REGRAS.map((r) => (
                            <tr key={r.titulo}>
                              <td>
                                <div className="po-nome">
                                  <i className={`fas ${r.icone} me-2 po-texto-${r.cor === 'cyan' ? 'navy' : r.cor}`}></i>
                                  {r.titulo}
                                </div>
                              </td>
                              <td>
                                <div>{r.prazo}</div>
                                <div className="po-sub mt-1">{r.detalhe}</div>
                              </td>
                              <td><Regra tipo="bad" icone="fa-circle-exclamation">{r.vermelho}</Regra></td>
                              <td><Regra tipo="warn" icone="fa-triangle-exclamation">{r.amarelo}</Regra></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="po-sub mt-3">
                      <i className="fas fa-circle-info me-1"></i>
                      O prazo para de correr quando o processo é entregue (checklist concluído, folha
                      fechada, benefício lançado). Atrasado de verdade é o que passou da data — só esse
                      entra na justificativa de INTERNO/EXTERNO ao abrir o módulo.
                    </div>
                  </div>
                  <div className="modal-footer bg-white">
                    <button type="button" className="btn btn-secondary" onClick={() => setPrazosAberto(false)}>Fechar</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {dados && semEscopo && (
          <div className="po-card po-enter text-center py-5" style={{ '--i': 0 }}>
            <div className="po-card-body pad">
              <i className="fas fa-user-lock po-texto-navy" style={{ fontSize: '2rem' }} aria-hidden="true"></i>
              <h3 className="mt-3 mb-1" style={{ fontSize: 18 }}>De quem é este painel?</h3>
              <p className="text-muted mb-4" style={{ maxWidth: 520, margin: '0 auto' }}>Seu usuário não está no cadastro de operadoras do BPO RH, então nada é carregado até você escolher.</p>
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                {(dados.operadores || []).map(o => <button key={o.id} type="button" className="btn btn-outline-primary px-4" onClick={() => trocarOperador(String(o.id))}><i className="fas fa-user me-2" aria-hidden="true"></i>{o.nome}</button>)}
                <button type="button" className="btn btn-light border px-4" onClick={() => trocarOperador('todos')} title="Todas as empresas, de todas as operadoras"><i className="fas fa-layer-group me-2" aria-hidden="true"></i>Todas as operadoras</button>
              </div>
            </div>
          </div>
        )}

        {dados && f && (
          <>
            <div className="po-contexto po-enter" style={{ '--i': 0 }}>
              <span><i className="fas fa-calendar-day me-1" aria-hidden="true"></i>Hoje {dados.hoje}</span>
              <span className="po-chip"><i className="fas fa-building" aria-hidden="true"></i>{dados.clientes.length} empresa{dados.clientes.length === 1 ? '' : 's'}</span>
              {dados.escopo === 'todos' && !dados.operador && <span><i className="fas fa-layer-group me-1" aria-hidden="true"></i>todas as operadoras</span>}
              {Object.keys(dados.erros || {}).length > 0 && (
                <span className="po-estado bad" title={Object.entries(dados.erros).map(([a, b]) => `${a}: ${b}`).join('\n')}><i className="fas fa-triangle-exclamation" aria-hidden="true"></i>{Object.keys(dados.erros).length} módulo(s) não respondeu</span>
              )}
            </div>

            {/* Feriados por vir, num painel próprio à direita (como no VA): feriado
                muda VA/VT, ponto e prazos — melhor ver chegando do que descobrir no dia. */}
            {(() => {
              const fers = (dados.feriados || []).filter((fe) => fe.geral || !empresasSel.length
                || (fe.cliente_ids || []).some((id) => empresasSel.includes(id)));
              if (!fers.length) {
                return (
                  <div className="d-flex justify-content-end mb-2 po-enter" style={{ '--i': 1 }}>
                    <button type="button" className="btn btn-sm btn-light border" onClick={() => setCalFeriados(true)}>
                      <i className="fas fa-umbrella-beach me-2" style={{ color: '#8a6d00' }} aria-hidden="true"></i>
                      Nenhum feriado nos próximos {dados.feriados_dias || 45} dias — abrir calendário
                    </button>
                  </div>
                );
              }
              const visiveis = fers.slice(0, 8);
              return (
                <div className="d-flex justify-content-end mb-2 po-enter" style={{ '--i': 1 }}>
                  <button type="button" className="bg-white rounded shadow-sm px-3 py-2 text-start border-0"
                    onClick={() => setCalFeriados(true)}
                    title="Abrir o calendário para adicionar, editar ou remover feriados"
                    style={{ border: '1px solid #f0dfa8', borderLeft: '4px solid #f1be48', minWidth: '340px', maxWidth: '460px', cursor: 'pointer' }}>
                    <div className="small fw-bold mb-1 d-flex justify-content-between align-items-center" style={{ color: '#8a6d00' }}>
                      <span>
                        <i className="fas fa-umbrella-beach me-2" aria-hidden="true"></i>
                        Feriados nos próximos {dados.feriados_dias || 45} dias
                      </span>
                      <span style={{ fontSize: '.78rem', fontWeight: 500 }}><i className="fas fa-pen me-1" aria-hidden="true"></i>editar</span>
                    </div>
                    {visiveis.map((fe) => (
                      <div key={`${fe.data_iso}-${fe.nome}`}
                        className="d-flex align-items-center justify-content-between gap-3 small"
                        style={{ color: fe.fim_de_semana ? '#adb5bd' : '#212529' }}>
                        <span>
                          <b style={{ fontVariantNumeric: 'tabular-nums' }}>{fe.data}</b>
                          <span className="text-muted"> ({fe.dia_semana})</span> {fe.nome}
                          {fe.cidade && <span className="text-muted" style={{ fontSize: '.85em' }}> · {fe.cidade}</span>}
                        </span>
                        <span className="badge text-truncate" style={{ maxWidth: 170, ...(fe.fim_de_semana
                          ? { background: '#f2f4f8', color: '#6c757d', border: '1px solid #dee2e6' }
                          : { background: '#fff5da', color: '#8a6d00', border: '1px solid #f0dfa8' }) }}
                          title={fe.geral ? 'Vale para todas as empresas' : (fe.clientes || []).join(', ')}>
                          {fe.geral ? 'todas as empresas' : (fe.clientes || []).join(', ')}
                        </span>
                      </div>
                    ))}
                    {fers.length > visiveis.length && (
                      <div className="po-sub small mt-1">+ {fers.length - visiveis.length} feriado(s) na janela — abra o calendário para ver todos</div>
                    )}
                  </button>
                </div>
              );
            })()}

            {/* férias começando em cima de feriado: a CLT veda — alerta vermelho */}
            {(() => {
              const conflitos = (dados.ferias_feriado || []).filter((x) => !empresasSel.length || empresasSel.includes(x.cliente_id));
              if (!conflitos.length) return null;
              return (
                <div className="alert alert-danger po-enter" style={{ '--i': 1 }} role="alert">
                  <div className="fw-bold mb-1">
                    <i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>
                    {conflitos.length === 1 ? 'Férias começando em cima de feriado' : `${conflitos.length} férias começando em cima de feriado`}
                  </div>
                  <div className="small mb-2">
                    A CLT (art. 134, §3º) <b>veda</b> o início das férias no feriado ou nos 2 dias que o antecedem.
                    Ajuste a data de início na Convenia antes de aprovar/pagar.
                  </div>
                  {conflitos.map((x) => (
                    <div key={`${x.cliente_id}-${x.convenia_id}-${x.inicio}`}
                      className="d-flex align-items-center justify-content-between gap-3 small py-1"
                      style={{ borderTop: '1px solid rgba(161,29,43,.15)' }}>
                      <span>
                        <b>{x.nome}</b> · {x.cliente} — início <b style={{ fontVariantNumeric: 'tabular-nums' }}>{x.inicio}</b>{' '}
                        {x.dias_antes === 0
                          ? <>em pleno <b>{x.feriado_nome}</b></>
                          : <>{x.dias_antes} dia{x.dias_antes === 1 ? '' : 's'} antes de <b>{x.feriado_nome}</b> ({x.feriado_data})</>}
                        {!x.aprovada && <span className="badge ms-2" style={{ background: '#fff5da', color: '#8a6d00', border: '1px solid #f1be48' }}>em aprovação</span>}
                      </span>
                      <Link to={x.link} className="btn btn-sm btn-outline-danger text-nowrap">
                        <i className="fas fa-arrow-right me-1" aria-hidden="true"></i>abrir em Férias
                      </Link>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* troca de cargo na Convenia: pedir à medicina o exame de mudança de função */}
            {(() => {
              const trocas = (dados.mudancas_cargo || []).filter((x) => !empresasSel.length || empresasSel.includes(x.cliente_id));
              if (!trocas.length && !mudCargoMsg) return null;
              return (
                <div className="alert alert-warning po-enter" style={{ '--i': 1 }} role="alert">
                  <div className="fw-bold mb-1">
                    <i className="fas fa-user-tie me-2" aria-hidden="true"></i>
                    {trocas.length === 1 ? 'Troca de cargo na Convenia' : `${trocas.length} trocas de cargo na Convenia`}
                    {trocas.length > 0 && ' — pedir o exame de mudança de função à medicina'}
                  </div>
                  {mudCargoMsg && <div className="small mb-1"><i className="fas fa-check me-1"></i>{mudCargoMsg}</div>}
                  {trocas.length > 0 && (
                    <div className="small mb-2">
                      Se os riscos da função mudaram, a NR-7 pede o exame antes da troca. Se não mudaram (ex.: promoção na mesma função), dispense.
                    </div>
                  )}
                  {trocas.map((x) => (
                    <div key={x.id} className="d-flex align-items-center justify-content-between gap-3 small py-1"
                      style={{ borderTop: '1px solid rgba(138,109,0,.18)' }}>
                      <span>
                        <b>{x.nome}</b> · {x.cliente} — {x.cargo_anterior} → <b>{x.cargo_novo}</b>
                        {x.data_alteracao && <> em <span style={{ fontVariantNumeric: 'tabular-nums' }}>{x.data_alteracao}</span></>}
                        {x.tem_rascunho && <span className="badge ms-2" style={{ background: '#eef7fb', color: '#0b6e86', border: '1px solid #9fd8e6' }}>texto salvo</span>}
                      </span>
                      <button type="button" className="btn btn-sm btn-outline-dark text-nowrap" onClick={() => { setMudCargoMsg(''); setMudCargo(x); }}>
                        <i className="fas fa-envelope me-1" aria-hidden="true"></i>Enviar e-mail à medicina
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}
            {mudCargo && (
              <ExameMudancaCargo pendencia={mudCargo} onFechar={() => setMudCargo(null)}
                onConcluido={(m) => { setMudCargo(null); setMudCargoMsg(m); carregar(true); }} />
            )}

            {/* filtros */}
            <div className="po-filtros po-enter" style={{ '--i': 1 }} role="group" aria-label="Filtros do painel">
              <label htmlFor="po-busca">Filtrar</label>
              <div className="po-empresas">
                {empresasComItens.map(c => (
                  <button key={c.id} type="button" className={`po-empresa ${empresasSel.includes(c.id) ? 'ativa' : ''}`} onClick={() => alternarEmpresa(c.id)}
                    aria-pressed={empresasSel.includes(c.id)} title={`${c.n} item(ns) em aberto`}>
                    {c.cliente}<span className="po-n">{c.n}</span>
                  </button>
                ))}
              </div>
              <label className="po-toggle ms-1"><input type="checkbox" checked={soEstourado} onChange={(e) => setSoEstourado(e.target.checked)} />só o que estourou</label>
              <input id="po-busca" type="search" className="po-busca" placeholder="Buscar colaborador ou empresa" value={busca} onChange={(e) => setBusca(e.target.value)} aria-label="Buscar colaborador ou empresa" />
              {filtroAtivo && <button type="button" className="btn btn-sm btn-light border" onClick={() => { setEmpresasSel([]); setSoEstourado(false); setBusca(''); }}><i className="fas fa-xmark me-1" aria-hidden="true"></i>Limpar</button>}
            </div>

            {/* KPIs em linha */}
            <div className="po-kpis po-enter" style={{ '--i': 2 }}>
              {kpi('sec-admissoes', 'fa-user-plus', 'Admissões', k.admNovas + k.admAndamento,
                <><b>{k.admNovas}</b> novas · <b>{k.admAndamento}</b> em andamento{k.admEstouradas ? <><br /><span className="bad">{k.admEstouradas} no prazo estourado</span></> : ''}</>, k.admEstouradas ? 'bad' : 'cyan')}
              {kpi('sec-rescisoes', 'fa-user-minus', 'Rescisões', k.resc, k.rescEstouradas ? <span className="bad">{k.rescEstouradas} no prazo estourado</span> : 'em aberto', k.rescEstouradas ? 'bad' : 'cyan')}
              {kpi('sec-folha', 'fa-file-invoice-dollar', 'Fechamento de folha', k.folhasAbertas,
                k.folhasAtrasadas ? <span className="bad">{k.folhasAtrasadas} fora do prazo</span> : (k.folhas ? 'folha(s) a fechar' : 'sem prazo cadastrado'), k.folhasAtrasadas ? 'bad' : 'cyan')}
              {kpi('sec-fechamento', 'fa-envelope-open-text', 'Fechamento ao cliente', k.fechPend,
                k.fechAtrasados ? <span className="bad">{k.fechAtrasados} passou do dia 5</span>
                  : <><b>{k.fechEnviados}</b> de {f.fechamentos.length} enviado(s)</>, k.fechAtrasados ? 'bad' : 'cyan')}
              {kpi('sec-ferias', 'fa-umbrella-beach', 'Férias na fila', k.ferFila,
                <>começam em até {ferias.dias_alerta || 30} dias · <b>{f.ferOperador.length}</b> comigo · <b>{f.ferGestor.length}</b> com o gestor</>,
                k.ferFila ? 'bad' : 'ok')}
              {kpi('sec-ferias', 'fa-hourglass-half', `Férias: solicitar em ${ferias.dias_alerta || 30}d`, k.ferFuturo,
                <>sem solicitação, no limite de {ferias.dias_alerta || 30} dias{k.ferVencidas ? <><br /><span className="bad">{k.ferVencidas} passaram do "Solicitar até"</span></> : ''}</>,
                (k.ferVencidas || k.ferFuturo) ? 'bad' : 'ok')}
              {kpi('sec-ponto', 'fa-fingerprint', 'Ponto: falta avisar', pontoTotais.carregando ? '…' : pontoTotais.faltam,
                pontoTotais.carregando ? `lendo ${pontoTotais.carregando} empresa(s)…` : <><b>{pontoTotais.avisados}</b> avisados · <b>{pontoTotais.pendentes}</b> com pendência</>, pontoTotais.faltam ? 'warn' : 'ok')}
              {kpi('sec-beneficios', 'fa-utensils', `VA · HO · VT até dia ${(dados.limite_beneficio || '25').slice(0, 2)}`, k.benPend,
                <>de <b>{k.benTotal}</b> · {k.benAtrasados ? <span className="bad">{k.benAtrasados} atrasado(s)</span> : `competência ${dados.competencia_beneficio}`}</>, k.benAtrasados ? 'bad' : (k.benPend ? 'warn' : 'ok'))}
              {kpi('sec-prorrogacoes', 'fa-user-clock', 'Experiência a vencer', k.pro,
                <>{k.proVencidas ? <span className="bad">{k.proVencidas} já vencido(s)</span> : '1º e 2º período em 15 dias'}{k.proSemTipo ? <><br /><b>{k.proSemTipo}</b> cadastrar na Convenia</> : ''}</>,
                k.proVencidas || k.proSemTipo ? 'bad' : 'warn')}
              {kpi('sec-contratos', 'fa-file-signature', 'Termo de prorrogação s/ PDF', k.contratos,
                <>1º período vencendo ou vencido{k.contratosVencidos ? <><br /><span className="bad">{k.contratosVencidos} já vencido(s)</span></> : ''}</>, k.contratosVencidos ? 'bad' : (k.contratos ? 'hot' : 'ok'))}
              {kpi('sec-exames', 'fa-notes-medical', 'Exame periódico vencido', k.exames,
                <>CLT e aprendiz com ASO fora do prazo{k.examesAgendados ? <><br /><b>{k.examesAgendados}</b> já com exame agendado</> : ''}</>, k.exames ? 'bad' : 'ok')}
              {kpi('sec-estagios', 'fa-graduation-cap', 'Estágios', k.estagios,
                <>{(k.estagiosTermo || k.estagiosTeto)
                  ? <span className="bad"><b>{k.estagiosTermo}</b> termo · <b>{k.estagiosTeto}</b> limite de {tetoAnos} anos</span>
                  : `termo em ${estagioDias} dias ou limite de ${tetoAnos} anos em ${estagioTetoDias} dias`}
                {k.estagiosSemContrato ? <><br /><b>{k.estagiosSemContrato}</b> sem contrato cadastrado</> : ''}</>,
                (k.estagiosTermo + k.estagiosTeto) ? 'bad' : (k.estagiosSemContrato ? 'warn' : 'ok'))}
            </div>

            {/* precisa de você hoje (crítico) + gráfico */}
            <div className="row g-4 mb-4">
              <div className="col-xl-7">
                <section className="po-card po-enter" style={{ '--i': 3 }} aria-labelledby="po-hoje-t">
                  <header className="po-card-head critico">
                    <h3 id="po-hoje-t"><i className={`fas ${alertas.nCritico ? 'fa-bell' : 'fa-mug-hot'}`} aria-hidden="true"></i>{alertas.nCritico ? 'Precisa de você hoje' : 'Nada estourado hoje'}</h3>
                    {alertas.nCritico > 0 && <span className="badge bg-danger"><i className="fas fa-fire me-1" aria-hidden="true"></i>{alertas.nCritico} crítico{alertas.nCritico === 1 ? '' : 's'}</span>}
                    <span className="po-sub">prazo estourado ou vencendo hoje · uma linha por colaborador</span>
                  </header>
                  <div className="po-card-body pad">
                    {alertas.nCritico === 0 ? <Vazio texto="Nenhum prazo estourado ou vencendo hoje para este filtro." /> : (
                      <div className="po-alerta-grupos">
                        <Grupo icone="fa-user-plus" rotulo="Admissões" itens={alertas.critico.admissoes} link={ROTAS.admissoes} cor="bad" />
                        <Grupo icone="fa-user-minus" rotulo="Rescisões" itens={alertas.critico.rescisoes} link={ROTAS.rescisoes} cor="bad" />
                        <Grupo icone="fa-umbrella-beach" rotulo="Férias com limite legal estourado (dobro)" itens={alertas.critico.ferias} link={ROTAS.ferias} cor="bad" />
                        <Grupo icone="fa-user-clock" rotulo="Experiência vencida" itens={alertas.critico.prorrogacoes} link={ROTAS.prorrogacoes} cor="bad" />
                        <Grupo icone="fa-utensils" rotulo="Benefícios fora do prazo" itens={alertas.critico.beneficios} link={ROTAS.beneficios} cor="bad" />
                        <Grupo icone="fa-file-signature" rotulo="Termo de prorrogação sem PDF (vencido)" itens={alertas.critico.contratos} link={ROTAS.prorrogacoes} cor="bad" />
                        <Grupo icone="fa-notes-medical" rotulo="Exame periódico vencido" itens={alertas.critico.exames} link={ROTAS.exames} cor="bad" />
                        <Grupo icone="fa-graduation-cap" rotulo={`Estágio: termo em ${estagioDias} dias ou limite de ${tetoAnos} anos em ${estagioTetoDias} dias`} itens={alertas.critico.estagios} link={ROTAS.estagios} cor="bad" />
                      </div>
                    )}
                  </div>
                </section>
              </div>
              <div className="col-xl-5">
                <section className="po-card po-enter" style={{ '--i': 4 }} aria-labelledby="po-carga-t">
                  <header className="po-card-head"><h3 id="po-carga-t"><i className="fas fa-chart-bar" aria-hidden="true"></i>Carga por módulo</h3><span className="po-sub">em aberto, por estado</span></header>
                  <div className="po-card-body pad" role="img" aria-label={carga.map(c => `${c.modulo}: ${c.total}`).join('; ')}
                    style={{ display: 'flex', flexDirection: 'column', minHeight: Math.max(420, carga.length * 52 + 80) }}>
                    {/* barras finas com ponta arredondada, 2px de respiro entre os segmentos
                        (traço branco), grade pontilhada recessiva, total em rótulo direto */}
                    {/* ocupa o card inteiro: o cartão ao lado é alto, e o gráfico cresce junto */}
                    <ResponsiveContainer width="100%" height="100%" minHeight={Math.max(400, carga.length * 52 + 60)}>
                      <BarChart data={carga} layout="vertical" margin={{ top: 12, right: 56, left: 8, bottom: 8 }} barSize={22} barCategoryGap={20}>
                        <CartesianGrid horizontal={false} strokeDasharray="2 4" stroke="#e6e8eb" />
                        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 13, fill: '#8a94a6' }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="modulo" width={140} tick={{ fontSize: 14.5, fill: '#212529', fontWeight: 600 }} axisLine={false} tickLine={false} />
                        <RechartsTooltip content={<CargaTooltip />} cursor={{ fill: 'rgba(0,32,91,.05)', radius: 6 }} />
                        <Legend content={<CargaLegenda />} />
                        <Bar dataKey="estourado" name="Estourado" stackId="a" fill={CHART.estourado} stroke="#fff" strokeWidth={2} isAnimationActive />
                        <Bar dataKey="alerta" name="Alerta" stackId="a" fill={CHART.alerta} stroke="#fff" strokeWidth={2} />
                        <Bar dataKey="emdia" name="Em dia" stackId="a" fill={CHART.emdia} stroke="#fff" strokeWidth={2} radius={[0, 9, 9, 0]}>
                          <LabelList dataKey="total" position="right" offset={10}
                            style={{ fontSize: 15, fontWeight: 700, fill: '#212529', fontVariantNumeric: 'tabular-nums' }}
                            formatter={(v) => (v ? v : '')} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              </div>
            </div>

            {/* atenção: pode ficar para outro momento */}
            <div className="row g-4 mb-4">
              <div className="col-12">
                <section className="po-card po-enter" style={{ '--i': 5 }} aria-labelledby="po-atencao-t">
                  <header className="po-card-head atencao">
                    <h3 id="po-atencao-t"><i className="fas fa-clock" aria-hidden="true"></i>Atenção — pode ficar para outro momento</h3>
                    {alertas.nAtencao > 0 && <span className="badge bg-warning text-dark">{alertas.nAtencao} em atenção</span>}
                    <span className="po-sub">vence nos próximos dias ou trava o fechamento, mas não é para hoje</span>
                  </header>
                  <div className="po-card-body pad">
                    {alertas.nAtencao === 0 ? <Vazio texto="Nada em atenção para este filtro." /> : (
                      <div className="po-alerta-grupos">
                        <Grupo icone="fa-user-plus" rotulo="Admissões" itens={alertas.atencao.admissoes} link={ROTAS.admissoes} cor="warn" />
                        <Grupo icone="fa-user-minus" rotulo="Rescisões" itens={alertas.atencao.rescisoes} link={ROTAS.rescisoes} cor="warn" />
                        <Grupo icone="fa-hourglass-half" rotulo={`Férias para solicitar em ${ferias.dias_alerta || 30} dias`} itens={alertas.atencao.ferias} link={ROTAS.ferias} cor="warn" />
                        <Grupo icone="fa-user-clock" rotulo="Experiência a vencer" itens={alertas.atencao.prorrogacoes} link={ROTAS.prorrogacoes} cor="warn" />
                        <Grupo icone="fa-utensils" rotulo="Benefícios perto do dia limite" itens={alertas.atencao.beneficios} link={ROTAS.beneficios} cor="warn" />
                        <Grupo icone="fa-file-signature" rotulo="Termo de prorrogação sem PDF" itens={alertas.atencao.contratos} link={ROTAS.prorrogacoes} cor="warn" />
                        <Grupo icone="fa-graduation-cap" rotulo="Estágio sem contrato cadastrado" itens={alertas.atencao.estagios} link={ROTAS.estagios} cor="warn" />
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>

            {/* módulos */}
            <div className="row g-4">
              <div className="col-xl-6">
                <Card id="sec-admissoes" icone="fa-user-plus" titulo="Admissões" indice={5} count={f.admissoes.length} countTipo={k.admEstouradas ? 'bad' : ''} link={ROTAS.admissoes}>
                  <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={f.admissoes} ordemInicial={{ col: 'prazo', dir: 'asc' }}
                    vazio="Nenhuma admissão em aberto para este filtro."
                    colunas={[colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                      { chave: 'data', rotulo: 'Admissão', valor: (l) => dataKey(l.data), classe: 'tabular', render: (l) => l.data || '—' },
                      colFase, colCheck, colPrazo, colSeta('Abrir a empresa em Admissões')]} />
                </Card>
              </div>
              <div className="col-xl-6">
                <Card id="sec-rescisoes" icone="fa-user-minus" titulo="Rescisões" indice={6} count={f.rescisoes.length} countTipo={k.rescEstouradas ? 'bad' : ''} link={ROTAS.rescisoes}>
                  <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={f.rescisoes} ordemInicial={{ col: 'prazo', dir: 'asc' }}
                    vazio="Nenhuma rescisão em aberto para este filtro."
                    colunas={[colPessoa(l => `${l.cargo || '—'}${l.vinculo ? ` · ${l.vinculo}` : ''}`), colEmpresa,
                      { chave: 'data', rotulo: 'Desligamento', valor: (l) => dataKey(l.data), classe: 'tabular', render: (l) => l.data || '—' },
                      colFase, colCheck, colPrazo, colSeta('Abrir a empresa em Rescisões')]} />
                </Card>
              </div>

              <div className="col-12">
                <Card id="sec-folha" icone="fa-file-invoice-dollar" titulo="Fechamento de folha" indice={7}
                  sub="prazo de cada empresa · alerta a 3 dias · vermelho quando passa"
                  count={k.folhasAbertas} countTipo={k.folhasAtrasadas ? 'bad' : ''} link="/Dp/Folha%20de%20Pagamento">
                  <Tabela chave={(l) => `${l.cliente_id}-${l.mes}`} linhas={f.folhas} ordemInicial={{ col: 'prazo', dir: 'asc' }}
                    vazio="Nenhuma empresa com prazo de folha cadastrado para este filtro (Configurações da folha → Prazo de fechamento)."
                    colunas={[colEmpresa,
                      { chave: 'competencia', rotulo: 'Competência', valor: (l) => l.mes, classe: 'tabular', render: (l) => l.competencia },
                      { chave: 'limite', rotulo: 'Fechar até', valor: (l) => dataKey(l.limite), classe: 'tabular',
                        render: (l) => (<><div>{l.limite}</div><div className="po-sub">{l.regra}</div></>) },
                      { chave: 'fechada', rotulo: 'Folha', valor: (l) => (l.total_filiais ? l.fechadas / l.total_filiais : 0),
                        render: (l) => (l.entregue
                          ? <Estado tipo="ok" icone="fa-lock">fechada</Estado>
                          : (l.total_filiais > 1
                            ? (<><Estado tipo="warn" icone="fa-lock-open">{l.fechadas}/{l.total_filiais} filiais</Estado>
                              <div className="po-sub mt-1">faltam {l.filiais.filter(x => !x.fechada).map(x => x.nome).join(', ')}</div></>)
                            : <Estado tipo="warn" icone="fa-lock-open">aberta</Estado>)) },
                      { chave: 'dp', rotulo: 'Enviada ao DP', valor: (l) => l.enviado_dp,
                        render: (l) => (l.enviado_dp
                          ? <Estado tipo="ok" icone="fa-paper-plane">{l.enviado_em}</Estado>
                          : <span className="po-sub">não</span>) },
                      colPrazo, colSeta('Abrir a folha desta competência')]} />
                </Card>
              </div>

              <div className="col-12">
                <Card id="sec-fechamento" icone="fa-envelope-open-text"
                  titulo={`Fechamento do mês ao cliente${f.fechamentos[0] ? ` — ${f.fechamentos[0].competencia}` : ''}`} indice={7}
                  sub="o fechamento do mês anterior vai ao cliente até o dia 5 · atenção a 3 dias · vermelho no dia e depois"
                  count={`${k.fechEnviados} de ${f.fechamentos.length}`} countTipo={k.fechAtrasados ? 'bad' : (k.fechPend ? 'warn' : 'ok')}
                  link="/BpoRh/FechamentoCliente" linkRotulo="Fechamento do mês">
                  <Tabela chave={(l) => `${l.cliente_id}-${l.mes}`} linhas={f.fechamentos} ordemInicial={{ col: 'sit', dir: 'asc' }}
                    vazio="Nenhuma empresa para este filtro."
                    colunas={[colEmpresa,
                      { chave: 'competencia', rotulo: 'Competência', valor: (l) => l.mes, classe: 'tabular', render: (l) => l.competencia },
                      { chave: 'limite', rotulo: 'Enviar até', valor: (l) => dataKey(l.limite), classe: 'tabular', render: (l) => l.limite },
                      { chave: 'sit', rotulo: 'Situação', valor: (l) => ({ atrasado: 0, hoje: 1, alerta: 2, ok: 3, nao_cobrado: 4, entregue: 5 }[l.situacao] ?? 6), render: (l) => (
                        l.situacao === 'entregue' ? <Estado tipo="ok" icone="fa-circle-check">enviado</Estado>
                          : l.situacao === 'nao_cobrado' ? <Estado tipo="neutro" icone="fa-minus">não enviado · sem cobrança</Estado>
                            : l.situacao === 'ok' ? <Estado tipo="neutro" icone="fa-clock">{fmtDias(l.dias)}</Estado>
                              : l.situacao === 'alerta' ? <Estado tipo="warn" icone="fa-triangle-exclamation">{fmtDias(l.dias)}</Estado>
                                : l.situacao === 'hoje' ? <Estado tipo="hot" icone="fa-bell">vence hoje</Estado>
                                  : <Estado tipo="bad" icone="fa-circle-exclamation">passou do dia {(l.limite || '').slice(0, 2)}</Estado>) },
                      { chave: 'envio', rotulo: 'Enviado', classe: 'po-sub', valor: (l) => l.enviado_em || '',
                        render: (l) => (l.enviado_em
                          ? <><div><i className="fas fa-envelope me-1" aria-hidden="true"></i>{l.enviado_em}</div>{l.enviado_por && <div>por {l.enviado_por}</div>}</>
                          : '—') },
                      colSeta('Abrir o Fechamento do mês')]} />
                </Card>
              </div>

              <div className="col-12">
                <Card id="sec-ferias" icone="fa-umbrella-beach" titulo="Férias" indice={7}
                  count={f.ferOperador.length + f.ferGestor.length + f.ferVencer.length + f.ferEstourados.length}
                  countTipo={f.ferEstourados.length ? 'bad' : (k.ferFuturo ? 'warn' : '')} link={ROTAS.ferias}>
                  <div className="po-tabs" role="tablist">
                    {[['operador', 'fa-user-check', 'Pendentes comigo', f.ferOperador.length], ['gestor', 'fa-user-tie', 'Aguardando o gestor', f.ferGestor.length],
                      ['vencer', 'fa-hourglass-half', `Solicitar até vence em ${ferias.dias_alerta || 30} dias`, f.ferVencer.length],
                      ['estourados', 'fa-circle-exclamation', 'Passou do "Solicitar até"', f.ferEstourados.length]].map(([id, ic, rot, n]) => (
                      <button key={id} type="button" role="tab" aria-selected={abaFerias === id} className={`po-tab ${abaFerias === id ? 'ativa' : ''}`} onClick={() => setAbaFerias(id)}>
                        <i className={`fas ${ic}`} aria-hidden="true"></i>{rot} <span className="po-n">{n}</span></button>
                    ))}
                  </div>
                  {abaFerias === 'vencer' || abaFerias === 'estourados' ? (
                    <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={abaFerias === 'vencer' ? f.ferVencer : f.ferEstourados} ordemInicial={{ col: 'limite', dir: 'asc' }}
                      vazio={abaFerias === 'vencer' ? 'Ninguém no prazo de solicitação sem férias marcadas para este filtro.' : 'Ninguém fora do prazo de solicitação neste filtro.'}
                      colunas={[colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                        { chave: 'periodos', rotulo: 'Períodos', valor: (l) => l.periodos || 1, classe: 'tabular', render: (l) => <span title={(l.concessivos || []).join(', ')}>{l.periodos || 1}</span> },
                        { chave: 'saldo', rotulo: 'Saldo', valor: (l) => l.saldo ?? -1, classe: 'tabular', render: (l) => (l.saldo != null ? `${l.saldo} d` : '—') },
                        { chave: 'limite', rotulo: 'Solicitar até', valor: (l) => (l.atrasado ? 1000 + Math.abs(l.prazo_dias ?? 0) : (l.prazo_dias ?? 999)), classe: 'tabular',
                          render: (l) => (<>{l.prazo_ate || l.concessivo_fim}<div className="po-sub">{fmtDias(l.prazo_dias, 'faltam', 'atrasado há')}{l.concessivo_fim ? ` · concessivo ${l.concessivo_fim}` : ''}</div></>) },
                        { chave: 'sit', rotulo: 'Situação', valor: (l) => (l.atrasado ? 0 : 1), render: (l) => (<>{l.atrasado ? <Estado tipo="bad" icone="fa-circle-exclamation">Atrasado</Estado> : <Estado tipo="warn" icone="fa-hourglass-half">A vencer</Estado>}{l.motivo_pendente && <div className="mt-1"><Estado tipo="hot" icone="fa-pen">falta o motivo do atraso</Estado></div>}</>) },
                        { chave: 'aviso', rotulo: 'Aviso ao cliente', render: (l) => (l.aviso_enviado_em ? <Estado tipo="ok" icone="fa-envelope-circle-check">{l.aviso_enviado_em}</Estado> : <span className="po-sub">não avisado</span>) },
                        colSeta('Abrir em Férias')]} />
                  ) : (
                    <Tabela chave={(l) => l.solicitacao_id || `${l.cliente_id}-${l.convenia_id}-${l.inicio}`} linhas={abaFerias === 'operador' ? f.ferOperador : f.ferGestor} ordemInicial={{ col: 'inicio', dir: 'asc' }}
                      vazio={abaFerias === 'gestor' ? 'Nenhuma solicitação parada no gestor.' : 'Nenhuma solicitação esperando você.'}
                      colunas={[colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                        { chave: 'inicio', rotulo: 'Período pedido', valor: (l) => dataKey(l.inicio), classe: 'tabular', render: (l) => (<>{l.inicio} → {l.fim}<div className="po-sub">{l.dias_solicitados} dias</div></>) },
                        { chave: 'etapa', rotulo: 'Etapa', valor: (l) => l.status, render: (l) => <Estado tipo={l.quem === 'gestor' ? 'neutro' : 'warn'} icone={l.quem === 'gestor' ? 'fa-user-tie' : 'fa-user-check'}>{STATUS_FERIAS[l.status] || l.status}</Estado> },
                        // dias_para_inicio = início − hoje: positivo ainda vai começar, negativo já começou
                        { chave: 'falta', rotulo: 'Início', valor: (l) => (l.dias_para_inicio ?? 9e9), classe: 'tabular', render: (l) => fmtDias(l.dias_para_inicio ?? null, 'faltam', 'começou há') },
                        { chave: 'pag', rotulo: 'Pagamento até', valor: (l) => dataKey(l.pagamento_ate), classe: 'tabular', render: (l) => l.pagamento_ate || '—' },
                        colSeta('Abrir em Férias')]} />
                  )}
                </Card>
              </div>

              <div className="col-xl-7">
                <Card id="sec-ponto" icone="fa-fingerprint" titulo={`Ponto · ${dados.mes_ponto.split('-').reverse().join('/')}`} indice={8} sub="quem não bateu ou bateu ímpar"
                  count={pontoTotais.carregando ? '…' : `${pontoTotais.faltam} falta avisar`} countTipo={pontoTotais.faltam ? 'warn' : 'ok'} link={ROTAS.ponto} linkRotulo="Resumo de Ponto">
                  {f.ponto.length === 0 ? <Vazio texto="Nenhuma empresa com sistema de ponto integrado neste filtro." /> : (
                    <div className="po-ponto-grid">
                      {f.ponto.map(p => {
                        const st = ponto[p.cliente_id] || {};
                        const d = st.dados;
                        const aberto = pontoAberto === p.cliente_id;
                        return (
                          <div key={p.cliente_id} className="po-ponto">
                            <div className="po-ponto-top">
                              <div><div className="po-nome">{p.cliente}</div><div className="po-sub">{p.sistema}{p.manual ? ' · importação manual' : ''}</div></div>
                              <Seta to={p.link} title={`Abrir o ponto da ${p.cliente}`} />
                            </div>
                            {st.carregando && <div className="mt-2" aria-busy="true"><div className="po-skel po-skel-linha" style={{ width: '70%' }}></div><div className="po-skel po-skel-linha" style={{ width: '45%' }}></div></div>}
                            {st.erro && <div className="mt-2"><Estado tipo="bad" icone="fa-triangle-exclamation">{st.erro}</Estado></div>}
                            {d?.indisponivel && (
                              <div className="mt-2">
                                <Estado tipo="neutro" icone={d.importar ? 'fa-file-import' : 'fa-circle-info'}>{d.importar ? 'precisa importar a planilha' : 'sem batidas no mês'}</Estado>
                                <div className="po-sub mt-1">{p.avisados ? `${p.avisados} aviso(s) registrado(s) · último ${p.ultimo_aviso}` : 'nenhum aviso registrado'}</div>
                              </div>
                            )}
                            {d && !d.indisponivel && (
                              <>
                                <div className="po-ponto-nums">
                                  <div><b className={d.faltam ? 'po-texto-warn' : 'po-texto-ok'}>{d.faltam}</b><small>falta avisar</small></div>
                                  <div><b>{d.avisados}</b><small>avisados</small></div>
                                  <div><b>{d.com_pendencia}</b><small>de {d.colaboradores} c/ pendência</small></div>
                                </div>
                                <div className="po-prog mt-2" title={`${d.avisados} de ${d.com_pendencia} avisados`}>
                                  <div className="po-prog-bar"><span className={d.faltam ? 'warn' : 'ok'} style={{ width: `${d.com_pendencia ? Math.round((d.avisados / d.com_pendencia) * 100) : 100}%` }}></span></div>
                                  <small>{d.sem_batida} sem batida · {d.impar} ímpar</small>
                                </div>
                                {d.faltam > 0 && (
                                  <>
                                    <button type="button" className="po-link-mais mt-1" aria-expanded={aberto} onClick={() => setPontoAberto(aberto ? null : p.cliente_id)}>
                                      <i className={`fas fa-chevron-${aberto ? 'up' : 'down'} me-1`} aria-hidden="true"></i>{aberto ? 'ocultar nomes' : 'ver quem falta'}</button>
                                    {aberto && (
                                      <div className="po-ponto-nomes">
                                        {d.faltam_lista.map((x, i) => <div key={i}>{x.nome} <span className="tabular">· {x.sem_batida} sem · {x.impar} ímpar</span></div>)}
                                        {d.faltam > d.faltam_lista.length && <div>… e mais {d.faltam - d.faltam_lista.length}</div>}
                                      </div>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </div>

              <div className="col-xl-5">
                <Card id="sec-beneficios" icone="fa-utensils" titulo={`VA · Home Office · VT — ${dados.competencia_beneficio}`} indice={9}
                  sub={`lançar até ${dados.limite_beneficio} · atenção a ${dados.alerta_beneficio_dias ?? 3} dias do limite · vermelho depois do dia ${(dados.limite_beneficio || '25').slice(0, 2)}`}
                  count={`${k.benPend} de ${k.benTotal}`} countTipo={k.benAtrasados ? 'bad' : (k.benPend ? 'warn' : 'ok')} link={ROTAS.beneficios} linkRotulo="Controle de VA">
                  <Tabela chave={(l) => `${l.tipo}-${l.cliente_id}`} linhas={f.beneficios} ordemInicial={{ col: 'sit', dir: 'asc' }}
                    vazio="Nenhuma empresa com VA, home office ou VT neste filtro."
                    colunas={[
                      { chave: 'tipo', rotulo: 'Benefício', valor: (l) => l.tipo, render: (l) => <Estado tipo="neutro" icone={l.tipo === 'va' ? 'fa-utensils' : l.tipo === 'vt' ? 'fa-bus' : 'fa-house-laptop'}>{l.tipo === 'va' ? 'VA' : l.tipo === 'vt' ? 'VT' : 'Home Office'}</Estado> },
                      colEmpresa,
                      { chave: 'sit', rotulo: 'Situação', valor: (l) => ({ atrasado: 0, hoje: 1, alerta: 2, ok: 3, entregue: 4 }[l.situacao] ?? 5), render: (l) => (
                        l.situacao === 'entregue' ? <Estado tipo="ok" icone="fa-circle-check">feito</Estado>
                          : l.situacao === 'ok' ? <Estado tipo="neutro" icone="fa-clock">{fmtDias(l.dias)}</Estado>
                            : l.situacao === 'alerta' ? <Estado tipo="warn" icone="fa-triangle-exclamation">{fmtDias(l.dias)}</Estado>
                              : l.situacao === 'hoje' ? <Estado tipo="hot" icone="fa-bell">vence hoje</Estado>
                                : <Estado tipo="bad" icone="fa-circle-exclamation">passou do dia {l.limite.slice(0, 2)}</Estado>) },
                      { chave: 'lanc', rotulo: 'Lançado', classe: 'po-sub', render: (l) => (<>{l.lancado && <div><i className="fas fa-check me-1" aria-hidden="true"></i>{l.lancado.colaboradores} colab. · {brl(l.lancado.valor)}</div>}{l.enviado && <div><i className="fas fa-envelope me-1" aria-hidden="true"></i>e-mail {l.enviado.em}</div>}{!l.lancado && !l.enviado && '—'}</>) },
                      colSeta('Abrir o controle da empresa')]} />
                </Card>
              </div>

              <div className="col-xl-7">
                <Card id="sec-prorrogacoes" icone="fa-user-clock" titulo="Prorrogação de experiência" indice={10} sub="vence em 15 dias, já venceu, ou sem tipo na Convenia"
                  count={f.prorrogacoes.length} countTipo={k.proVencidas || k.proSemTipo ? 'bad' : (k.pro ? 'warn' : '')} link={ROTAS.prorrogacoes}>
                  <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={f.prorrogacoes} ordemInicial={{ col: 'prox', dir: 'asc' }}
                    vazio="Nenhum período de experiência cobrando ação neste filtro."
                    colunas={[colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                      { chave: 'adm', rotulo: 'Admissão', valor: (l) => dataKey(l.data_admissao), classe: 'tabular', render: (l) => l.data_admissao || '—' },
                      { chave: 'exp', rotulo: 'Experiência', valor: (l) => (l.sem_tipo ? 0 : 1), render: (l) => (l.sem_tipo
                        ? <Estado tipo="bad" icone="fa-triangle-exclamation" title="O tipo do período de experiência não está preenchido na Convenia (Colaborador → Contrato → Período de experiência)">cadastrar na Convenia</Estado>
                        : <span className="po-sub">{l.tipo}</span>) },
                      { chave: 'prox', rotulo: 'Períodos', valor: (l) => l.proximo?.dias_para_fim ?? 999, render: (l) => (
                        <div className="po-marcos">
                          {l.marcos.map(m => {
                            const tipo = m.avisado_em ? 'ok' : m.vencido ? 'bad' : m.alerta ? 'warn' : 'neutro';
                            const ic = m.avisado_em ? 'fa-envelope-circle-check' : m.vencido ? 'fa-circle-exclamation' : m.alerta ? 'fa-triangle-exclamation' : 'fa-clock';
                            return <Estado key={m.numero} tipo={tipo} icone={ic} title={m.avisado_em ? `Gestor avisado em ${m.avisado_em}` : `${m.numero}º período termina em ${m.fim}`}>{m.numero}º · {m.fim} · {fmtDias(m.dias_para_fim, 'em', 'venceu há')}</Estado>;
                          })}
                          {l.contrato_pendente && <Estado tipo="hot" icone="fa-file-signature">termo não anexado</Estado>}
                        </div>) },
                      colSeta('Abrir em Prorrogação')]} />
                </Card>
              </div>

              <div className="col-xl-5">
                <Card id="sec-contratos" icone="fa-file-signature" titulo="Termo de prorrogação sem PDF" indice={11} sub="1º período vencendo em 15 dias ou já vencido — quem fica assina o termo"
                  count={f.contratos.length} countTipo={k.contratosVencidos ? 'bad' : (f.contratos.length ? 'warn' : 'ok')} link={ROTAS.prorrogacoes}>
                  <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={f.contratos} ordemInicial={{ col: 'marco', dir: 'asc' }}
                    vazio="Nenhum termo de prorrogação pendente neste filtro."
                    colunas={[
                      colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                      { chave: 'adm', rotulo: 'Admissão', valor: (l) => dataKey(l.data_admissao), classe: 'tabular', render: (l) => l.data_admissao || '—' },
                      { chave: 'marco', rotulo: '1º período', valor: (l) => ((l.marcos || []).find(m => m.pendente_contrato) || {}).dias_para_fim ?? 999, render: (l) => {
                        const m1 = (l.marcos || []).find(m => m.pendente_contrato) || {};
                        return m1.vencido
                          ? <Estado tipo="bad" icone="fa-circle-exclamation">{m1.fim} · {fmtDias(m1.dias_para_fim, 'em', 'venceu há')}</Estado>
                          : <Estado tipo="warn" icone="fa-triangle-exclamation">{m1.fim} · {fmtDias(m1.dias_para_fim, 'em')}</Estado>;
                      } },
                      colSeta('Abrir em Prorrogação')]} />
                </Card>
              </div>

              <div className="col-12">
                <Card id="sec-exames" icone="fa-notes-medical" titulo="Exame periódico vencido" indice={12}
                  sub="NR-7 · só CLT e Jovem Aprendiz · vencimento pela Convenia ou pela periodicidade da empresa"
                  count={f.exames.length} countTipo={f.exames.length ? 'bad' : 'ok'} link={ROTAS.exames} linkRotulo="Exame Periódico">
                  <Tabela chave={(l) => `${l.cliente_id}-${l.convenia_id}`} linhas={f.exames} ordemInicial={{ col: 'venc', dir: 'asc' }}
                    vazio="Nenhum exame periódico vencido neste filtro."
                    colunas={[
                      colPessoa(l => `${l.cargo || '—'}${l.departamento ? ` · ${l.departamento}` : ''}`), colEmpresa,
                      { chave: 'adm', rotulo: 'Admissão', valor: (l) => dataKey(l.admissao), classe: 'tabular', render: (l) => l.admissao || '—' },
                      { chave: 'ult', rotulo: 'Último exame', valor: (l) => dataKey(l.ultimo_exame), classe: 'tabular', render: (l) => l.ultimo_exame || <span className="po-sub">nenhum</span> },
                      { chave: 'venc', rotulo: 'Venceu em', valor: (l) => l.dias_para_vencer ?? 0, render: (l) => (
                        <><Estado tipo="bad" icone="fa-circle-exclamation">{l.vencimento} · {fmtDias(l.dias_para_vencer, 'faltam', 'há')}</Estado>
                          <div className="po-sub">{l.vencimento_origem === 'convenia' ? 'data da Convenia' : `calculado pela ${l.vencimento_origem}`}{l.agendado_para ? ` · agendado ${l.agendado_para}` : ''}</div></>) },
                      colSeta('Abrir em Exame Periódico')]} />
                </Card>
              </div>

              <div className="col-12">
                <Card id="sec-finrec" icone="fa-money-check-dollar" titulo="Financeiro — pagamentos a comunicar" indice={13}
                  sub="férias aprovadas, rescisões e guias mensais (FGTS etc.) ainda não enviadas ao financeiro do cliente · amarelo a 3 dias · vermelho atrasado"
                  count={f.finRec.length} countTipo={f.finRec.some(l => (l.dias_para_prazo ?? 0) < 0) ? 'bad' : (f.finRec.length ? 'warn' : 'ok')}
                  link="/BpoRh/Financeiro" linkRotulo="Financeiro">
                  <Tabela chave={(l) => `${l.cliente_id}-${l.chaveFin}`} linhas={f.finRec} ordemInicial={{ col: 'prazo', dir: 'asc' }}
                    vazio="Nada a comunicar ao financeiro vencendo ou atrasado neste filtro."
                    colunas={[
                      { chave: 'oque', rotulo: 'O quê', valor: (l) => l.oque, render: (l) => (
                        <Estado tipo="neutro" icone={l.oque === 'Férias' ? 'fa-umbrella-beach' : l.oque === 'Rescisão' ? 'fa-user-minus' : 'fa-rotate'}>{l.oque}</Estado>) },
                      { chave: 'tarefa', rotulo: 'Pessoa / tarefa', valor: (l) => norm(l.nome), render: (l) => (<><div className="po-nome">{l.nome}</div><div className="po-sub">{l.detalhe}</div></>) },
                      colEmpresa,
                      { chave: 'vence', rotulo: 'Pagar / enviar até', valor: (l) => dataKey(l.data), classe: 'tabular', render: (l) => l.data || '—' },
                      { chave: 'prazo', rotulo: 'Prazo', valor: (l) => l.dias_para_prazo ?? 999, render: (l) => {
                        const d = l.dias_para_prazo;
                        return d === null || d === undefined ? <span className="po-sub">—</span>
                          : d < 0 ? <Estado tipo="bad" icone="fa-circle-exclamation">atrasado {fmtDias(d, 'faltam', 'há')}</Estado>
                            : d === 0 ? <Estado tipo="hot" icone="fa-bell">vence hoje</Estado>
                              : d <= 3 ? <Estado tipo="warn" icone="fa-triangle-exclamation">vence em {d}d</Estado>
                                : <Estado tipo="neutro" icone="fa-clock">vence em {d}d</Estado>;
                      } },
                      colSeta('Abrir a empresa no Financeiro')]} />
                </Card>
              </div>

              {(dados?.pontomais_ferias || []).length > 0 || f.pmFerias.length > 0 ? (
              <div className="col-12">
                <Card id="sec-pmferias" icone="fa-umbrella-beach" titulo="Férias no ponto (Convenia × PontoMais)" indice={13}
                  sub={`férias aprovadas na Convenia que faltam no PontoMais, com datas diferentes ou sobrando lá · ponto lido em ${
                    [...new Set(f.pmFerias.map(x => x.espelho_em).filter(Boolean))].join(', ') || '—'}`}
                  count={f.pmFerias.length} countTipo={f.pmFerias.some(l => l.tipo === 'faltando') ? 'bad' : (f.pmFerias.length ? 'warn' : 'ok')}>
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                    {[...new Map(f.pmFerias.map(x => [x.cliente_id, x.cliente])).entries()].map(([cid, nome]) => (
                      <button key={cid} type="button" className="btn btn-sm btn-outline-secondary" disabled={!!pmOcupado}
                        onClick={() => acaoPmFerias({ acao: 'atualizar', cliente_id: cid }, `at-${cid}`)}
                        title="Relê as ausências de todos os colaboradores no PontoMais">
                        <i className={`fas ${pmOcupado === `at-${cid}` ? 'fa-spinner fa-spin' : 'fa-rotate'} me-1`}></i>Atualizar do PontoMais · {nome}
                      </button>
                    ))}
                    {pmMsg && <span className="po-sub">{pmMsg}</span>}
                  </div>
                  <Tabela chave={(l) => `${l.cliente_id}-${l.tipo}-${l.convenia_id || l.pontomais_id}-${l.convenia || l.ponto}`} linhas={f.pmFerias}
                    ordemInicial={{ col: 'sit', dir: 'asc' }}
                    vazio="Convenia e PontoMais batem neste filtro."
                    colunas={[
                      { chave: 'sit', rotulo: 'Situação', valor: (l) => ({ faltando: 0, datas: 1, sobrando: 2 }[l.tipo] ?? 3), render: (l) => (
                        <Estado tipo={l.tipo === 'faltando' ? 'bad' : 'warn'} icone={l.tipo === 'faltando' ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'}>{l.rotulo}</Estado>) },
                      { chave: 'nome', rotulo: 'Colaborador', valor: (l) => norm(l.nome), render: (l) => <div className="po-nome">{l.nome}</div> },
                      colEmpresa,
                      { chave: 'conv', rotulo: 'Convenia', valor: (l) => l.convenia || '', classe: 'tabular', render: (l) => l.convenia || <span className="po-sub">—</span> },
                      { chave: 'ponto', rotulo: 'PontoMais', valor: (l) => l.ponto || '', classe: 'tabular', render: (l) => l.ponto || <span className="po-sub">—</span> },
                      { chave: 'acao', rotulo: '', estilo: { width: 150 }, render: (l) => (l.tipo !== 'sobrando' && l.convenia_id ? (
                        <button type="button" className="btn btn-sm btn-outline-primary" disabled={!!pmOcupado}
                          onClick={() => acaoPmFerias({ acao: 'enviar', cliente_id: l.cliente_id, convenia_id: l.convenia_id }, `en-${l.convenia_id}`)}
                          title="Lança/ajusta as férias deste colaborador no PontoMais agora">
                          <i className={`fas ${pmOcupado === `en-${l.convenia_id}` ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`}></i>Enviar ao ponto
                        </button>) : <span className="po-sub">remova no PontoMais</span>) },
                      { chave: 'ignorar', rotulo: '', estilo: { width: 96 }, render: (l) => (l.tipo !== 'sobrando' && l.convenia_id ? (
                        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={!!pmOcupado}
                          onClick={() => setPmIgnorar({ linha: l, motivo: 'Sócio — não bate ponto' })}
                          title="Tirar este colaborador da conferência (ex.: sócio que não bate ponto)">
                          <i className="fas fa-eye-slash me-1"></i>Ignorar
                        </button>) : null) },
                    ]} />
                {pmIgnorar && (
                    <div className="border rounded p-2 mt-2 d-flex flex-wrap gap-2 align-items-center" style={{ background: '#f8f9fa' }}>
                      <span className="small">Ignorar <b>{pmIgnorar.linha.nome}</b> ({pmIgnorar.linha.cliente}) na conferência de férias do ponto. Motivo:</span>
                      <input className="form-control form-control-sm" style={{ maxWidth: 280 }} value={pmIgnorar.motivo}
                        onChange={(e) => setPmIgnorar(x => ({ ...x, motivo: e.target.value }))} />
                      <button type="button" className="btn btn-sm btn-primary" disabled={!pmIgnorar.motivo.trim() || !!pmOcupado}
                        onClick={() => { acaoPmFerias({ acao: 'ignorar', cliente_id: pmIgnorar.linha.cliente_id, convenia_id: pmIgnorar.linha.convenia_id, motivo: pmIgnorar.motivo }, `ig-${pmIgnorar.linha.convenia_id}`); setPmIgnorar(null); }}>
                        Confirmar
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setPmIgnorar(null)}>Cancelar</button>
                    </div>
                  )}
                </Card>
              </div>
              ) : null}

              <div className="col-12">
                <Card id="sec-estagios" icone="fa-graduation-cap" titulo="Estágio" indice={13}
                  sub={`Lei 11.788 · termo em ${estagioDias} dias ou vencido · limite de ${tetoAnos} anos em ${estagioTetoDias} dias (não cabe renovar) · sem contrato cadastrado`}
                  count={f.estagios.length} countTipo={(k.estagiosTermo + k.estagiosTeto) ? 'bad' : (k.estagiosSemContrato ? 'warn' : 'ok')} link={ROTAS.estagios} linkRotulo="Controle de Estágio">
                  <Tabela chave={(l) => `${l.cliente_id}-${l.chave || l.convenia_id || l.nome}`} linhas={f.estagios} ordemInicial={{ col: 'motivo', dir: 'asc' }}
                    vazio="Nenhum estágio cobrando ação neste filtro."
                    colunas={[
                      colPessoa(l => l.instituicao || '—'), colEmpresa,
                      { chave: 'motivo', rotulo: 'Situação', valor: (l) => ({ teto: 0, termo: 1, sem_contrato: 2 }[l.motivo] ?? 3) * 10000 + (l.dias ?? 9999), render: (l) => (
                        l.motivo === 'teto'
                          ? <><Estado tipo="bad" icone="fa-scale-balanced" title={l.teto_estimado ? 'sem admissão na Convenia — calculado pelo primeiro termo' : `Limite legal de ${tetoAnos} anos (Lei 11.788, art. 11)`}>limite de {tetoAnos} anos · {fmtDias(l.dias, 'em', 'atingido há')}{l.teto_estimado ? ' *' : ''}</Estado>
                            <div className="po-sub" style={{ color: '#b3261e' }}>não cabe renovação</div></>
                          : l.motivo === 'termo'
                            ? <Estado tipo="bad" icone="fa-hourglass-half">termo · {fmtDias(l.dias, 'em', 'vencido há')}</Estado>
                            : <Estado tipo="warn" icone="fa-file-circle-question">sem contrato cadastrado</Estado>) },
                      { chave: 'fim', rotulo: 'Fim do termo', valor: (l) => dataKey(l.fim), classe: 'tabular', render: (l) => l.fim || '—' },
                      { chave: 'teto', rotulo: `Limite de ${tetoAnos} anos`, valor: (l) => dataKey(l.teto), classe: 'tabular',
                        render: (l) => (l.teto ? <span title={l.teto_estimado ? 'sem admissão na Convenia — calculado pelo primeiro termo' : undefined}>{l.teto}{l.teto_estimado ? ' *' : ''}</span> : '—') },
                      colSeta('Abrir em Controle de Estágio')]} />
                </Card>
              </div>
            </div>
          </>
        )}
      </div>

      {calFeriados && (
        <CalendarioFeriados fechar={() => setCalFeriados(false)} aoMudar={() => carregar(true)} />
      )}

      {/* ─── Pop-up: rescisões atrasadas há mais de 10 dias → sugerir finalização manual ─── */}
      {sugestaoFinalizar && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10003 }}
          onClick={(e) => { if (e.target === e.currentTarget) fecharSugestao(); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header bg-white">
                <div>
                  <h5 className="modal-title" style={{ color: '#212529' }}>
                    <i className="fas fa-flag-checkered me-2" style={{ color: '#c1153d' }} aria-hidden="true"></i>
                    {sugestaoFinalizar.length === 1 ? 'Rescisão atrasada' : `${sugestaoFinalizar.length} rescisões atrasadas`} há mais de {ATRASO_SUGERE_FINALIZACAO} dias
                  </h5>
                  <div className="small" style={{ color: '#6c757d' }}>
                    Se ela não vai fechar pelo fluxo normal (colaborador não assina, processo na justiça, desistência),
                    peça à gestora a <b>finalização manual</b>.
                  </div>
                </div>
                <button type="button" className="btn-close" onClick={fecharSugestao} aria-label="Fechar"></button>
              </div>
              <div className="modal-body p-0">
                <table className="table table-sm align-middle mb-0">
                  <thead className="table-light">
                    <tr><th className="ps-3">Empresa</th><th>Colaborador</th><th>Desligamento</th><th>Atraso</th><th>Parada em</th><th></th></tr>
                  </thead>
                  <tbody>
                    {sugestaoFinalizar.map((it) => (
                      <tr key={`${it.cliente_id}-${it.convenia_id}`}>
                        <td className="ps-3 fw-semibold">{it.cliente}</td>
                        <td>{it.nome}</td>
                        <td className="small">{it.data || '—'}</td>
                        <td><span className="badge" style={{ background: '#fde8ec', color: '#a11d2b' }}>há {it.atraso} dias</span></td>
                        <td className="small text-muted">
                          {it.proxima || '—'}
                          {it.pendentes ? <> · {it.pendentes} pendente(s)</> : null}
                        </td>
                        <td className="text-end pe-3">
                          <Link className="btn btn-sm btn-primary text-nowrap"
                            to={`${it.link}&finalizar=${it.convenia_id}&pedir=1`} onClick={fecharSugestao}>
                            <i className="fas fa-paper-plane me-1" aria-hidden="true"></i>Pedir finalização
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer bg-white d-flex justify-content-between">
                <span className="small text-muted">
                  O pedido vai por e-mail à gestora, com prévia antes de enviar. Só o adm finaliza.
                </span>
                <button type="button" className="btn btn-sm btn-light border" onClick={fecharSugestao}>Agora não</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PainelOperador;
